// Floor-plan walls. Vertex-based so polyline joints stay connected: walls
// reference shared vertices, and moving a vertex updates every wall on it.
// Each wall is a GROUP anchored at its start vertex, rebuilt as box PIECES that
// leave gaps for its openings (doors/windows); windows also get a glass pane.

import * as THREE from "three";

import { markFloorMutation } from "./history";

export const WALL_HEIGHT = 11 * 0.3048; // 11 ft, in metres
const OCC_MAX_EDGES = 64; // footprint edges the see-through shader can test
export const WALL_THICKNESS = 0.1;
const DOOR_LEAF_THICK = 0.04; // door leaf slab thickness (metres)
const HANDLE_R = 0.09;
// Corner lock-marker radius. Sized to match the measurement vertex's dot:line
// ratio (its dot radius equals the ribbon width), so here the dot radius equals
// the wall thickness — just scaled up from the measure dot because the wall is
// thicker than the ribbon. The caller scales markers by 1.5, so divide it out:
// 0.0667 × 1.5 = 0.1 = WALL_THICKNESS final.
const CORNER_R = WALL_THICKNESS / 1.5;
const MITER_LIMIT = WALL_THICKNESS * 8; // clamp miter spikes at acute joins

type P2 = { x: number; z: number };
// A wall incident to a vertex, as a unit ray leaving that vertex.
type Inc = { wid: number; ux: number; uz: number; ang: number };

// Scratch for updateOcclusionFade's world→wall-local mapping (see there). Reused
// per frame so the per-frame occlusion pass allocates nothing.
const _occInv = new THREE.Matrix4();
const _occCam = new THREE.Vector3();

// Do segments p1p2 and p3p4 (in XZ) properly cross? Used by the see-through test
// to ask whether a sight-line passes through a room-boundary wall.
function segHit(
  p1x: number, p1z: number, p2x: number, p2z: number,
  p3x: number, p3z: number, p4x: number, p4z: number,
): boolean {
  const d1 = (p2x - p1x) * (p3z - p1z) - (p2z - p1z) * (p3x - p1x);
  const d2 = (p2x - p1x) * (p4z - p1z) - (p2z - p1z) * (p4x - p1x);
  const d3 = (p4x - p3x) * (p1z - p3z) - (p4z - p3z) * (p1x - p3x);
  const d4 = (p4x - p3x) * (p2z - p3z) - (p4z - p3z) * (p2x - p3x);
  return d1 * d2 < 0 && d3 * d4 < 0;
}

// Shared GLSL for the see-through wall shaders. The room is the footprint polygon
// (wall centerlines, uFoot) extruded from y=0 to y=uWallH. A wall fragment fades
// when the eye→fragment ray, continued PAST the fragment, passes through that
// room volume — i.e. the wall is standing between the viewer and some part of the
// interior (floor, an object, or a far wall). occInsideFoot is the XZ parity test
// against the footprint; occRayHitsRoom does the ray/prism test.
const OCC_GLSL = `
uniform vec3 uCam;
uniform vec4 uFoot[${OCC_MAX_EDGES}];
uniform int uFootCount;
uniform float uWallH;
bool occInsideFoot(vec2 F) {
  bool inside = false;
  for (int i = 0; i < ${OCC_MAX_EDGES}; i++) {
    if (i >= uFootCount) break;
    vec4 e = uFoot[i];
    if ((e.y > F.y) != (e.w > F.y)) {
      float xc = e.x + (F.y - e.y) / (e.w - e.y) * (e.z - e.x);
      if (xc > F.x) inside = !inside;
    }
  }
  return inside;
}
bool occSegHit(vec2 p1, vec2 p2, vec2 p3, vec2 p4) {
  float d1 = (p2.x - p1.x) * (p3.y - p1.y) - (p2.y - p1.y) * (p3.x - p1.x);
  float d2 = (p2.x - p1.x) * (p4.y - p1.y) - (p2.y - p1.y) * (p4.x - p1.x);
  float d3 = (p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x);
  float d4 = (p4.x - p3.x) * (p2.y - p3.y) - (p4.y - p3.y) * (p2.x - p3.x);
  return d1 * d2 < 0.0 && d3 * d4 < 0.0;
}
bool occRayHitsRoom(vec3 P) {
  if (uFootCount <= 0) return false;
  float dy = P.y - uCam.y;
  float tLo, tHi;
  if (abs(dy) > 1e-4) {
    // t where the ray crosses the floor (y=0) and the wall top (y=uWallH);
    // [tLo,tHi] is the slice of the ray inside the room's height band.
    float t0 = (0.0 - uCam.y) / dy;
    float tH = (uWallH - uCam.y) / dy;
    tLo = min(t0, tH); tHi = max(t0, tH);
  } else {
    tLo = 1.0; tHi = 201.0; // ray ~level with the fragment: march out within the band
  }
  tLo = max(tLo, 1.0001); // only PAST the fragment (t=1 is the fragment itself)
  if (tLo >= tHi) return false;
  vec2 A = uCam.xz + tLo * (P.xz - uCam.xz);
  vec2 B = uCam.xz + tHi * (P.xz - uCam.xz);
  if (occInsideFoot(A) || occInsideFoot(B)) return true; // ends inside the footprint
  for (int i = 0; i < ${OCC_MAX_EDGES}; i++) {        // or the segment crosses a wall
    if (i >= uFootCount) break;
    vec4 e = uFoot[i];
    if (occSegHit(A, B, e.xy, e.zw)) return true;
  }
  return false;
}
`;

// Extruded quad prism: footprint corners c0→c1→c2→c3 (XZ, loop order) raised
// from y0 to y1. Built in WORLD coords; DoubleSide material renders it
// regardless of winding so a mitered (non-rectangular) end can't go invisible.
// The 16 prism vertex positions, shared by the from-scratch build and the
// in-place relayout so the two paths can never disagree.
// Side ring first: the four vertical faces SHARE their corner verts, so smooth
// normals blend across each vertical edge instead of leaving a hard seam line
// down the corner. (Hemisphere light keys off normal.y, which is ~0 for every
// vertical face, so this blend adds no shading gradient — just kills the seam.)
// Then top + bottom caps with their OWN verts so their normals stay flat
// (0,±1,0): both triangles of a cap shade identically, so the split diagonal
// never reads as a crease — which is the only reason flat shading used to be
// forced.
function prismPositions(
  c0: P2, c1: P2, c2: P2, c3: P2,
  y0: number, y1: number,
): number[] {
  const c = [c0, c1, c2, c3];
  const pos: number[] = [];
  for (const p of c) pos.push(p.x, y0, p.z); // 0..3 bottom ring
  for (const p of c) pos.push(p.x, y1, p.z); // 4..7 top ring
  for (const p of c) pos.push(p.x, y1, p.z); // 8..11 top cap
  for (const p of c) pos.push(p.x, y0, p.z); // 12..15 bottom cap
  return pos;
}

function prismGeometry(
  c0: P2,
  c1: P2,
  c2: P2,
  c3: P2,
  y0: number,
  y1: number,
  // End caps are the two vertical faces that span the wall THICKNESS (c1→c2 at
  // the far edge, c3→c0 at the near edge). Through a see-through wall such a cap
  // is a single faded layer, so it reads DARKER than the two-layer flat wall
  // around it — the "sliver" at opening jambs. We drop a cap wherever the edge
  // is an interior seam (flush against a neighbouring segment): the flat faces
  // there already meet, and a single reveal quad covers the opening span. Caps
  // are kept only at true wall ends. capEnd = c1→c2, capStart = c3→c0.
  capStart = true,
  capEnd = true,
): THREE.BufferGeometry {
  const pos = prismPositions(c0, c1, c2, c3, y0, y1);
  const idx: number[] = [];
  for (let i = 0; i < 4; i++) {
    if (i === 1 && !capEnd) continue; // far-edge thickness cap (c1→c2)
    if (i === 3 && !capStart) continue; // near-edge thickness cap (c3→c0)
    const n = (i + 1) % 4;
    idx.push(i, n, 4 + n, i, 4 + n, 4 + i); // side quad
  }
  idx.push(8, 9, 10, 8, 10, 11); // top cap
  idx.push(12, 14, 13, 12, 15, 14); // bottom cap
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
}

// Jamb reveal quad positions (see addReveal in planWall).
function revealPositions(pl: P2, pr: P2, y0: number, y1: number): number[] {
  return [pl.x, y0, pl.z, pr.x, y0, pr.z, pr.x, y1, pr.z, pl.x, y1, pl.z];
}

// Solve A x = b for a small dense system (Gauss–Jordan with partial pivoting).
// Used by the angle-preserving length solve; A is symmetric positive-definite
// (λI + Σ w·aaᵀ) so it's never singular in practice. Returns null if it is.
function solveLinear(A: number[][], b: number[]): number[] | null {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]); // augmented
  for (let col = 0; col < n; col++) {
    let piv = col;
    for (let r = col + 1; r < n; r++) {
      if (Math.abs(M[r][col]) > Math.abs(M[piv][col])) piv = r;
    }
    if (Math.abs(M[piv][col]) < 1e-12) return null;
    [M[col], M[piv]] = [M[piv], M[col]];
    const d = M[col][col];
    for (let r = 0; r < n; r++) {
      if (r === col) continue;
      const f = M[r][col] / d;
      if (f === 0) continue;
      for (let c = col; c <= n; c++) M[r][c] -= f * M[col][c];
    }
  }
  return M.map((row, i) => row[n] / row[i]);
}

export type OpeningKind = "door" | "window";
export interface Opening {
  id: number;
  kind: OpeningKind;
  offset: number; // center distance from the wall start (metres)
  width: number;
  height: number;
  sill: number; // bottom above the floor (0 = sits on the floor; doors default here)
  lockLeft?: boolean; // left jamb pinned along the wall (edits keep it fixed)
  lockRight?: boolean; // right jamb pinned
  lockTop?: boolean; // top edge pinned (height edits keep it fixed)
  lockBottom?: boolean; // bottom edge / sill pinned (windows only)
  // Doors only — which way the leaf hangs + swings (drives the leaf panel + the
  // floor swing arc). Defaults: hinge at the LEFT jamb, swinging to the wall's
  // left-normal side. Flip either with the door-flip shortcuts.
  hingeRight?: boolean; // hinge at the right jamb instead of the left
  swingNeg?: boolean; // swing to the right-normal side instead of the left
}

export type OpeningEdge = "left" | "right" | "top" | "bottom";

export const OPENING_DEFAULTS: Record<OpeningKind, Omit<Opening, "id" | "offset">> = {
  // ADA-standard door leaf: 36" × 80" (3'-0" × 6'-8").
  door: { kind: "door", width: 0.9144, height: 2.032, sill: 0 },
  window: { kind: "window", width: 1.2, height: 1.2, sill: 0.9 },
};

export interface WallInfo {
  id: number;
  ax: number;
  az: number;
  bx: number;
  bz: number;
  length: number;
}

// A wall's computed build plan: every emitted vertex position plus a
// structural key (piece kinds/caps, pane/leaf/swing counts, border length).
// layout() compares the key against the wall's previous build — a drag that
// only MOVES the wall (same pieces, same opening structure) writes the new
// positions into the existing geometry buffers instead of disposing and
// rebuilding every mesh of the wall on every drag frame. Both paths consume
// the same plan, so their position math cannot drift apart.
type PiecePlan =
  | {
      kind: "prism";
      c0: P2; c1: P2; c2: P2; c3: P2;
      y0: number; y1: number;
      capStart: boolean; capEnd: boolean;
    }
  | { kind: "reveal"; pl: P2; pr: P2; y0: number; y1: number };
interface BoxPlan {
  sx: number; sy: number; sz: number; // scale
  px: number; py: number; pz: number; // position
  rotY: number;
}
interface WallPlan {
  key: number[];
  proj: [P2, P2, P2, P2];
  pieces: PiecePlan[];
  panes: BoxPlan[];
  leaves: BoxPlan[];
  swings: number[][]; // arc line positions per door
  border: number[];
}
function planKeysEq(a: number[] | undefined, b: number[]): boolean {
  if (!a || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}
// Write freshly computed positions into an existing geometry and let its
// bounds recompute lazily (raycasting reads the bounding sphere).
function writePositions(geo: THREE.BufferGeometry, pos: number[]): void {
  const attr = geo.getAttribute("position") as THREE.BufferAttribute;
  (attr.array as Float32Array).set(pos);
  attr.needsUpdate = true;
  geo.boundingSphere = null;
  geo.boundingBox = null;
}

interface WallEntry {
  a: number; // vertex id
  b: number;
  group: THREE.Group;
  pieces: THREE.Mesh[]; // structural box pieces (rebuilt on layout)
  depthPieces: THREE.Mesh[]; // depth-only twins of `pieces` for the occlusion pre-pass
  panes: THREE.Mesh[]; // window glass
  leaves: THREE.Mesh[]; // door leaf panels (rebuilt on layout)
  swings: THREE.LineSegments[]; // door floor swing arcs (rebuilt on layout)
  openings: Opening[];
  proj?: THREE.Mesh; // flat ground footprint (shown when walls are hidden)
  border?: THREE.LineSegments; // black outline around each opening (rebuilt on layout)
  // Structural key of the last build (see WallPlan) — gates the in-place
  // relayout fast path.
  planKey?: number[];
  // Which side the dimension is measured/displayed on: 0 = centerline,
  // +1 = left face, -1 = right face (drag the on-wall pill to change it).
  dimSide: DimSide;
  // True once the user has dragged this wall's pill — then dimSide is frozen
  // to their choice. While false, connected walls auto-default to their inner
  // (room-interior) face via autoOrientDimSides().
  dimSideExplicit: boolean;
}

// 0 = centerline, +1 = left (left-normal) face, -1 = right face.
export type DimSide = -1 | 0 | 1;

// How far outward (metres) a face dimension line + pill sit beyond the wall face.
const DIM_GAP = 0.12;

// On-wall dimension annotation. The dimension line (sx,sz)→(ex,ez) and pill
// (x,z) sit OUTSIDE the measured face (across the wall) to keep the room clear;
// witness lines run from each end back across the wall to (wsx,wsz)/(wex,wez),
// reaching the measured (inside) face so it stays clear what's being measured.
export interface WallLabelInfo {
  x: number;
  z: number;
  length: number;
  sx: number;
  sz: number;
  ex: number;
  ez: number;
  wsx: number; // witness inner endpoints (at the measured face)
  wsz: number;
  wex: number;
  wez: number;
  side: DimSide;
  id: number;
}

const PROJ_Y = 0.02; // ground-projection height (just above the floor)

// Flat (single-quad) footprint on the ground from 4 XZ corners.
function flatQuad(c0: P2, c1: P2, c2: P2, c3: P2): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(
      [c0.x, PROJ_Y, c0.z, c1.x, PROJ_Y, c1.z, c2.x, PROJ_Y, c2.z, c3.x, PROJ_Y, c3.z],
      3,
    ),
  );
  g.setIndex([0, 1, 2, 0, 2, 3]);
  return g;
}

export class WallSystem {
  readonly group = new THREE.Group();
  // Flat ground footprints, shown only while the 3D walls are hidden so the
  // user keeps a reference of where the walls are. Sibling of `group` (not a
  // child) so hiding the walls doesn't hide it.
  readonly projGroup = new THREE.Group();
  private projMat = new THREE.MeshBasicMaterial({
    color: 0x8a93a8,
    transparent: true,
    opacity: 0.4,
    side: THREE.DoubleSide,
    depthWrite: false,
  });
  private verts = new Map<number, { x: number; z: number }>();
  private walls = new Map<number, WallEntry>();
  private nextV = 1;
  private nextW = 1;
  private nextO = 1;
  private selected: number | null = null;
  // Layout-wide wall height (metres), uniform across every wall. Adjustable via
  // setHeight(); all geometry + markers read this.
  private wallHeight = WALL_HEIGHT;
  // Marquee multi-selection: these walls draw highlighted (selMat) too.
  private highlighted = new Set<number>();

  // Presentation-rig roof (see setCeilingMode). Derived from the wall graph —
  // never persisted; rebuilt lazily (updateCeilingIfDirty) when walls change.
  private ceilingMode: "off" | "ghost" | "solid" = "off";
  private ceilingDirty = true;
  private ceiling: THREE.Mesh | null = null;
  private ceilingSolidMat = new THREE.MeshStandardMaterial({
    color: 0xd8dbe4,
    roughness: 0.95,
    side: THREE.DoubleSide, // underside is what the walkthrough actually sees
  });
  // Invisible to the camera but still drawn into the sun's shadow map (the
  // shadow pass uses its own depth material, so colorWrite doesn't matter
  // there) — the desktop Lighting preview keeps its top-down view INTO rooms
  // while interiors still go dark except where openings admit the sun.
  // shadowSide must be explicit: the shadow pass renders the REVERSE of
  // material.side by default, and the roof is a single-sided up-facing plane
  // — its reversed faces point away from the sun and get culled, i.e. the
  // sun shines straight through the roof.
  private ceilingGhostMat = new THREE.MeshBasicMaterial({
    colorWrite: false,
    depthWrite: false,
    shadowSide: THREE.DoubleSide,
  });
  // Room floor (same rasterized footprint as the roof, at ground level):
  // off-black, so interiors stay in the app's dark aesthetic and the window
  // light patches read as the bright accents.
  private roomFloor: THREE.Mesh | null = null;
  private roomFloorMat = new THREE.MeshStandardMaterial({
    color: 0x232428,
    roughness: 0.85,
  });
  // A room floor decoupled from the presentation ceiling/lighting rig: same
  // wall-bounded footprint as `roomFloor`, but toggled on its own (setRoomFloor)
  // so it can show in the plain editing rig. The AR layout tabletop uses it to
  // give the room a real floor without turning on the roof + sun. Mutually
  // exclusive with the presentation `roomFloor` in practice (one rig at a time).
  private soloFloor: THREE.Mesh | null = null;
  // See-through ceiling: an enclosed room is automatically roofed at wall-top
  // height with a slab that fades exactly like the walls — it shares occMat and
  // the per-frame occlusion uniforms, so the same per-pixel test (occRayHitsRoom)
  // turns it transparent wherever it stands between the orbit camera and the room
  // interior. Built from the same wall-bounded footprint as the presentation
  // roof, but driven by the see-through pass (showSeeThruCeiling) rather than the
  // lighting rig: no toggle, present for any closed room while you look into it in
  // the 3D orbit. Rebuilt lazily when the wall graph changes (seeThruCeilingDirty),
  // at most once per frame while shown.
  private seeThruCeiling: THREE.Mesh | null = null;
  private seeThruCeilingDirty = true;

  private geo = new THREE.BoxGeometry(1, 1, 1); // unit; pieces scale it
  // Smooth shading: prismGeometry shares the side verts (so vertical corners
  // blend, no seam line) and splits the caps onto their own flat-normal verts
  // (so the top quad's diagonal doesn't crease). No flatShading needed.
  private mat = new THREE.MeshStandardMaterial({
    color: 0xcfd3df,
    roughness: 0.95,
    side: THREE.DoubleSide,
  });
  private selMat = new THREE.MeshStandardMaterial({
    color: 0x6fa0ff,
    roughness: 0.9,
    side: THREE.DoubleSide,
  });
  private glassMat = new THREE.MeshStandardMaterial({
    color: 0x9fd0ff,
    transparent: true,
    opacity: 0.32,
    roughness: 0.1,
  });
  // Door leaf panel — same cool-grey as the walls; a black edge outline
  // (doorLeafEdgeGeo + doorLeafBorderMat below) makes the open leaf read
  // distinctly without a wood tone. DoubleSide: the thin slab is seen from both
  // faces.
  private doorLeafMat = new THREE.MeshStandardMaterial({
    color: 0xcfd3df,
    roughness: 0.7,
    side: THREE.DoubleSide,
  });
  // Black border traced around every edge of the leaf box, so the wall-coloured
  // leaf still stands out against the walls. Geometry is the shared unit box's
  // edges; it inherits each leaf's scale/position/rotation as a child mesh.
  private doorLeafEdgeGeo = new THREE.EdgesGeometry(this.geo);
  private doorLeafBorderMat = new THREE.LineBasicMaterial({ color: 0x000000 });
  // Floor swing arc — a light line on the (dark) room floor / ground grid,
  // tracing the leaf's path from closed to open.
  private doorSwingMat = new THREE.LineBasicMaterial({
    color: 0xcfd3df,
    transparent: true,
    opacity: 0.7,
    depthWrite: false,
  });
  // See-through variants of the door leaf + its black outline. A wall fades with
  // occMat's PER-PIXEL test because its fragments sit ON the room boundary ("is
  // there room behind me?" → near wall fades, far wall stays solid). The open leaf
  // swings OFF that boundary into the interior, where the same test is ill-defined
  // (almost anything has room beyond it), so it can't drive the leaf. Instead the
  // leaf gets a flat uniform fade, switched per-door by updateOcclusionFade only
  // when a SOLID wall actually stands between the camera and the leaf (wallBetween)
  // — so the leaf reads exactly like the wall you're peering through to see it, and
  // stays solid when you view it through the open doorway or from inside the room.
  // opacity 0.16 == occUniforms.uFade; depthWrite off so it doesn't occlude the
  // interior behind it (matching occMat).
  private doorLeafFadeMat = new THREE.MeshStandardMaterial({
    color: 0xcfd3df,
    roughness: 0.7,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.16,
    depthWrite: false,
  });
  private doorLeafBorderFadeMat = new THREE.LineBasicMaterial({
    color: 0x000000,
    transparent: true,
    opacity: 0.16,
    depthWrite: false,
  });
  // See-through material driven PER PIXEL: a wall fragment fades only where the
  // sight-line through it carries on into the room interior (see occRayHitsRoom),
  // so just the parts of a wall actually standing in front of the room turn
  // transparent (the rest stays solid). Uniforms (camera + footprint polygon +
  // wall height) are refreshed each frame by updateOcclusionFade. depthWrite off
  // so faded pixels don't occlude the interior.
  private occFootprint = Array.from({ length: OCC_MAX_EDGES }, () => new THREE.Vector4());
  private occUniforms = {
    uCam: { value: new THREE.Vector3() },
    uFoot: { value: this.occFootprint },
    uFootCount: { value: 0 },
    uWallH: { value: WALL_HEIGHT },
    uFade: { value: 0.16 },
    // World→wall-local matrix. The see-through test runs in the wall group's
    // LOCAL space (uFoot/uWallH are local), so the vertex shader maps each
    // fragment's world position into local with this before the fragment test;
    // updateOcclusionFade keeps it and uCam in sync. Identity in the desktop
    // views (group at world origin); non-trivial when the layout is shown
    // transformed, e.g. the AR tabletop scales/positions it onto the desk.
    uOccToLocal: { value: new THREE.Matrix4() },
  };
  // Patch a stock three material so the see-through test runs per pixel: share the
  // occUniforms, carry the world position to the fragment shader as vOccW, and run
  // `action` wherever occRayHitsRoom(vOccW) is true (fade the pixel, or discard).
  private occShader(action: string) {
    return (shader: THREE.WebGLProgramParametersWithUniforms): void => {
      Object.assign(shader.uniforms, this.occUniforms);
      shader.vertexShader =
        "varying vec3 vOccW;\nuniform mat4 uOccToLocal;\n" +
        shader.vertexShader.replace(
          "#include <begin_vertex>",
          "#include <begin_vertex>\n  vOccW = (uOccToLocal * modelMatrix * vec4(transformed, 1.0)).xyz;",
        );
      shader.fragmentShader =
        `varying vec3 vOccW;\n${OCC_GLSL}\nuniform float uFade;\n` +
        shader.fragmentShader.replace(
          "vec4 diffuseColor = vec4( diffuse, opacity );",
          `vec4 diffuseColor = vec4( diffuse, opacity );
  if (occRayHitsRoom(vOccW)) { ${action} }`,
        );
    };
  }
  private occMat = ((): THREE.MeshStandardMaterial => {
    const m = new THREE.MeshStandardMaterial({
      color: 0xcfd3df,
      roughness: 0.95,
      side: THREE.DoubleSide,
      transparent: true,
      depthWrite: false,
    });
    m.onBeforeCompile = this.occShader("diffuseColor.a *= uFade;");
    return m;
  })();
  // Depth-only twin of occMat: writes depth for the SOLID pixels of a see-through
  // wall (discarding the faded ones) so they occlude the background grid dots,
  // which rely on walls writing depth. colorWrite off — it only fills the depth
  // buffer, in the opaque pass (before the transparent dots). Shares occMat's
  // uniforms; faded pixels discard so depth stays open and the dots show through.
  private occDepthMat = ((): THREE.MeshBasicMaterial => {
    const m = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, colorWrite: false });
    m.onBeforeCompile = this.occShader("discard;");
    return m;
  })();
  // See-through variant of the window glass: same tint as glassMat, but its alpha
  // drops to uFade wherever the sight-line carries on into the room — so a window
  // pane fades away with the wall around it instead of floating as a brighter
  // rectangle. depthWrite off (like occMat) so faded panes don't occlude the
  // interior. (A window pane sits in the wall plane, so occRayHitsRoom classifies
  // it correctly — unlike a door leaf, which swings off it; see doorLeafFadeMat.)
  private occGlassMat = ((): THREE.MeshStandardMaterial => {
    const m = new THREE.MeshStandardMaterial({
      color: 0x9fd0ff,
      transparent: true,
      opacity: 0.32,
      roughness: 0.1,
      depthWrite: false,
    });
    m.onBeforeCompile = this.occShader("diffuseColor.a *= uFade;");
    return m;
  })();
  // Opening-frame outline that fades with the wall: a black line that drops to the
  // wall's `uFade` opacity wherever the see-through test says it stands in front of
  // the interior — so the head/sill/jamb lines don't stay solid black over a
  // transparent wall. Shares occMat's uniforms (uFootCount is 0 outside 3D, so the
  // line stays fully solid in the plan/elevation views).
  private occBorderMat = new THREE.ShaderMaterial({
    uniforms: this.occUniforms,
    transparent: true,
    depthWrite: false,
    vertexShader: `varying vec3 vOccW;
uniform mat4 uOccToLocal;
void main() {
  vOccW = (uOccToLocal * modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`,
    fragmentShader: `varying vec3 vOccW;
${OCC_GLSL}
uniform float uFade;
void main() {
  float a = occRayHitsRoom(vOccW) ? uFade : 1.0;
  gl_FragColor = vec4(0.0, 0.0, 0.0, a);
}`,
  });

  private preview = new THREE.Mesh(
    this.geo,
    new THREE.MeshStandardMaterial({ color: 0x6fa0ff, transparent: true, opacity: 0.5 }),
  );
  private handleGeo = new THREE.SphereGeometry(HANDLE_R * 0.9, 14, 10); // match lock markers
  // transparent (opacity 1, so still opaque-looking) + no depth write: this puts
  // the markers in the transparent render pass so they draw OVER the transparent
  // grid + dimension lines instead of being shown through by them.
  private handleMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  });
  private handles = [
    new THREE.Mesh(this.handleGeo, this.handleMat),
    new THREE.Mesh(this.handleGeo, this.handleMat),
  ];

  constructor() {
    this.projGroup.visible = false; // walls shown by default → no projection
    this.preview.visible = false;
    this.preview.renderOrder = 5;
    this.group.add(this.preview);
    for (const h of this.handles) {
      h.visible = false;
      h.renderOrder = 19;
      this.group.add(h);
    }
  }

  // ── Vertices ──────────────────────────────────────────────────────────────
  private addVertex(x: number, z: number): number {
    const id = this.nextV++;
    this.verts.set(id, { x, z });
    return id;
  }
  vertexNear(x: number, z: number, eps: number): number | null {
    for (const [id, v] of this.verts) {
      if (Math.hypot(v.x - x, v.z - z) <= eps) return id;
    }
    return null;
  }
  vertexPos(id: number): { x: number; z: number } | undefined {
    return this.verts.get(id);
  }
  vertexPositions(): { x: number; z: number }[] {
    return [...this.verts.values()];
  }
  // Vertices with their ids — for screen-space corner picking (in-place locking).
  vertexEntries(): { id: number; x: number; z: number }[] {
    const out: { id: number; x: number; z: number }[] = [];
    for (const [id, v] of this.verts) out.push({ id, x: v.x, z: v.z });
    return out;
  }
  // Every door opening as {wallId, openingId} — entry points for the VR
  // walkthrough (it positions the player at one of these).
  doorList(): { wallId: number; openingId: number }[] {
    const out: { wallId: number; openingId: number }[] = [];
    for (const [wallId, w] of this.walls) {
      for (const o of w.openings) {
        if (o.kind === "door") out.push({ wallId, openingId: o.id });
      }
    }
    return out;
  }
  // True if world XZ point (x,z) is inside the room footprint — a parity ray-cast
  // (+X) over closed-loop wall edges (endpoints shared by ≥2 walls), the same test
  // the see-through shader uses. Lets the walkthrough face the door INTO the room.
  isInsideRoom(x: number, z: number): boolean {
    const deg = new Map<number, number>();
    for (const [, w] of this.walls) {
      deg.set(w.a, (deg.get(w.a) ?? 0) + 1);
      deg.set(w.b, (deg.get(w.b) ?? 0) + 1);
    }
    let crossings = 0;
    for (const [, w] of this.walls) {
      if ((deg.get(w.a) ?? 0) < 2 || (deg.get(w.b) ?? 0) < 2) continue;
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      if (a.z > z === b.z > z) continue;
      const xc = a.x + ((z - a.z) / (b.z - a.z)) * (b.x - a.x);
      if (xc > x) crossings++;
    }
    return (crossings & 1) === 1;
  }

  // ── Presentation roof + room floor ────────────────────────────────────
  // One rasterized room footprint feeds two slabs: a roof at wall-top height
  // so the sun only enters through door/window openings, and a warm floor at
  // ground level. Roof "solid" = the visible white ceiling the VR walkthrough
  // stands under; "ghost" = camera-invisible but still a shadow caster, for
  // the desktop Lighting preview (the editor keeps looking into rooms from
  // above while interiors light like interiors).
  setCeilingMode(mode: "off" | "ghost" | "solid"): void {
    this.ceilingMode = mode;
    this.rebuildCeiling();
  }
  // The footprint raster only depends on vertex positions + wall height, but
  // the dirty flags are set by EVERY layout() — including opening drags, which
  // relayout their wall each frame without moving the footprint at all. So the
  // lazy rebuild paths compare this signature first and clear the flag without
  // rasterizing when the footprint inputs are unchanged.
  private ceilingSig: number[] | null = null;
  private seeThruSig: number[] | null = null;
  private footprintSig(): number[] {
    const sig: number[] = [this.wallHeight];
    for (const w of this.walls.values()) {
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      sig.push(a.x, a.z, b.x, b.z);
    }
    return sig;
  }
  private static sigEq(a: number[] | null, b: number[]): boolean {
    if (!a || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  }
  // Lazy rebuild, called once per floor frame: wall drags relayout
  // continuously, so the footprint rasterization runs at most once a frame
  // and only while a roof is actually shown (and only when the footprint
  // actually moved — see footprintSig).
  updateCeilingIfDirty(): void {
    if (!this.ceilingDirty || this.ceilingMode === "off") return;
    if (WallSystem.sigEq(this.ceilingSig, this.footprintSig())) {
      this.ceilingDirty = false;
      return;
    }
    this.rebuildCeiling();
  }
  private rebuildCeiling(): void {
    this.ceilingDirty = false;
    this.ceilingSig = this.footprintSig();
    if (this.ceiling) {
      this.group.remove(this.ceiling);
      this.ceiling.geometry.dispose(); // shared with roomFloor — dispose once
      this.ceiling = null;
    }
    if (this.roomFloor) {
      this.group.remove(this.roomFloor);
      this.roomFloor = null;
    }
    if (this.ceilingMode === "off") return;
    const geom = this.buildFootprintGeometry();
    if (!geom) return;
    this.ceiling = new THREE.Mesh(
      geom,
      this.ceilingMode === "solid" ? this.ceilingSolidMat : this.ceilingGhostMat,
    );
    this.ceiling.position.y = this.wallHeight;
    this.ceiling.castShadow = true;
    this.ceiling.raycast = () => {}; // roof never intercepts wall/floor picking
    this.group.add(this.ceiling);
    // Room floor: same footprint at ground level, warm and always visible in
    // both modes — interiors stand on an actual floor instead of the dark
    // outdoor ground disc, and the window shafts land on it.
    this.roomFloor = new THREE.Mesh(geom, this.roomFloorMat);
    this.roomFloor.position.y = 0.001; // over the ground disc, under the dots
    this.roomFloor.receiveShadow = true;
    this.roomFloor.raycast = () => {};
    this.group.add(this.roomFloor);
  }
  // Build (on=true) / remove (on=false) the presentation-independent room floor
  // — a flat slab bounded by the room's walls (the same interior footprint the
  // presentation roof uses). Returns true if a floor now exists; false when the
  // layout has no closed room (the rasterizer needs closed loops), so the caller
  // can fall back to another ground. Built once on demand: the AR tabletop's
  // walls don't change mid-session, so there's no per-frame rebuild path.
  setRoomFloor(on: boolean): boolean {
    if (this.soloFloor) {
      this.group.remove(this.soloFloor);
      this.soloFloor.geometry.dispose();
      this.soloFloor = null;
    }
    if (!on) return false;
    // dilate=false: keep the floor inside the wall centerline so its edge is
    // hidden under the wall, not poking out past it.
    const geom = this.buildFootprintGeometry(false);
    if (!geom) return false;
    const mesh = new THREE.Mesh(geom, this.roomFloorMat);
    mesh.position.y = 0.001; // just above ground level, under the wall bottoms
    mesh.receiveShadow = true;
    mesh.raycast = () => {}; // never intercepts wall / model picking
    this.group.add(mesh);
    this.soloFloor = mesh;
    return true;
  }
  // See-through ceiling: show (on=true, in the 3D orbit) or hide the auto roof.
  // Rebuilds the slab lazily — only when shown AND the wall graph changed since
  // the last build — so a continuous wall drag rasterizes the footprint at most
  // once a frame. The fade is per-pixel in occMat, refreshed every frame by
  // updateOcclusionFade (which the caller runs alongside this), so the shared
  // occlusion uniforms are already current when the ceiling renders. Called every
  // floor frame from the same gate as the wall fade (3D view, not walkthrough,
  // walls on); hidden everywhere else so it never blocks the top-down plan view.
  showSeeThruCeiling(on: boolean): void {
    if (!on) {
      if (this.seeThruCeiling) this.seeThruCeiling.visible = false;
      return;
    }
    if (this.seeThruCeilingDirty) {
      // Also covers the open-room case (build produced no slab): as long as
      // the footprint inputs haven't moved, don't re-rasterize every frame.
      if (WallSystem.sigEq(this.seeThruSig, this.footprintSig())) {
        this.seeThruCeilingDirty = false;
      } else {
        this.rebuildSeeThruCeiling();
      }
    }
    if (this.seeThruCeiling) this.seeThruCeiling.visible = true;
  }
  private rebuildSeeThruCeiling(): void {
    this.seeThruCeilingDirty = false;
    this.seeThruSig = this.footprintSig();
    if (this.seeThruCeiling) {
      this.group.remove(this.seeThruCeiling);
      this.seeThruCeiling.geometry.dispose(); // occMat is shared — never disposed
      this.seeThruCeiling = null;
    }
    // Dilate by exactly the centerline→outer-face distance (WALL_THICKNESS / 2) so
    // the roof reaches the outer wall faces and stops there — not a coarse cell past.
    const geom = this.buildFootprintGeometry(true, WALL_THICKNESS / 2);
    if (!geom) return; // no closed room → no ceiling (rebuilds when one closes)
    const mesh = new THREE.Mesh(geom, this.occMat);
    mesh.position.y = this.wallHeight;
    mesh.raycast = () => {}; // never intercepts wall / floor / model picking
    this.group.add(mesh);
    this.seeThruCeiling = mesh;
  }
  // Roof footprint as merged quads: rasterize the room-interior parity test
  // over the wall bounds, dilate one ring so the slab tucks into the wall
  // tops (no light-leak gap along edges), then greedy-merge runs into
  // rectangles. Only closed loops count (same rule as isInsideRoom), so a
  // half-drawn room has no roof until its walls close — which doubles as
  // feedback in the preview.
  //
  // `dilate` grows the slab outward by ONE cell from the wall centerline, so the
  // cell size sets how far past the centerline it reaches: the default ~0.15 m
  // cell overshoots a thin wall, so a caller wanting the edge to land on the
  // OUTER wall face passes minStep = WALL_THICKNESS / 2 (the centerline→outer-face
  // distance), shrinking the dilation ring to match.
  private buildFootprintGeometry(dilate = true, minStep = 0.15): THREE.BufferGeometry | null {
    // Closed-loop edges, gathered once (isInsideRoom rebuilds them per query).
    const deg = new Map<number, number>();
    for (const [, w] of this.walls) {
      deg.set(w.a, (deg.get(w.a) ?? 0) + 1);
      deg.set(w.b, (deg.get(w.b) ?? 0) + 1);
    }
    const edges: number[] = []; // ax, az, bx, bz
    let minX = Infinity;
    let maxX = -Infinity;
    let minZ = Infinity;
    let maxZ = -Infinity;
    for (const [, w] of this.walls) {
      if ((deg.get(w.a) ?? 0) < 2 || (deg.get(w.b) ?? 0) < 2) continue;
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      edges.push(a.x, a.z, b.x, b.z);
      minX = Math.min(minX, a.x, b.x);
      maxX = Math.max(maxX, a.x, b.x);
      minZ = Math.min(minZ, a.z, b.z);
      maxZ = Math.max(maxZ, a.z, b.z);
    }
    if (edges.length === 0) return null;
    const PAD = 0.3; // covers the one-cell dilation at the bounds
    minX -= PAD;
    minZ -= PAD;
    maxX += PAD;
    maxZ += PAD;
    // Cell size: minStep (≈0.15 m by default) unless a finer dilation is asked
    // for; clamped so a pathological layout can't explode the grid (400/side cap).
    const step = Math.max(minStep, (maxX - minX) / 400, (maxZ - minZ) / 400);
    const nx = Math.ceil((maxX - minX) / step);
    const nz = Math.ceil((maxZ - minZ) / step);
    const inside = new Uint8Array(nx * nz);
    for (let iz = 0; iz < nz; iz++) {
      const pz = minZ + (iz + 0.5) * step;
      for (let ix = 0; ix < nx; ix++) {
        const px = minX + (ix + 0.5) * step;
        let crossings = 0;
        for (let e = 0; e < edges.length; e += 4) {
          const az = edges[e + 1];
          const bz = edges[e + 3];
          if (az > pz === bz > pz) continue;
          const xc = edges[e] + ((pz - az) / (bz - az)) * (edges[e + 2] - edges[e]);
          if (xc > px) crossings++;
        }
        if (crossings & 1) inside[iz * nx + ix] = 1;
      }
    }
    // Dilate one ring (roof): the roof overlaps the wall tops rather than
    // stopping a fraction of a cell short of them (which would leak a thin sun
    // stripe). The room FLOOR passes dilate=false instead — it should stop at
    // the wall centerline so the wall's own thickness hides the floor's edge,
    // rather than poking out past the outer wall face.
    let filled: Uint8Array;
    if (dilate) {
      filled = new Uint8Array(nx * nz);
      for (let iz = 0; iz < nz; iz++) {
        for (let ix = 0; ix < nx; ix++) {
          let any = 0;
          for (let dz = -1; dz <= 1 && !any; dz++) {
            for (let dx = -1; dx <= 1 && !any; dx++) {
              const jx = ix + dx;
              const jz = iz + dz;
              if (jx >= 0 && jx < nx && jz >= 0 && jz < nz && inside[jz * nx + jx]) any = 1;
            }
          }
          filled[iz * nx + ix] = any;
        }
      }
    } else {
      filled = inside;
    }
    // Greedy rectangle merge over the filled cells.
    const pos: number[] = [];
    const quad = (x0: number, z0: number, x1: number, z1: number): void => {
      const y = 0; // built at ground level; the ceiling/floor meshes offset in y
      pos.push(x0, y, z0, x0, y, z1, x1, y, z1, x0, y, z0, x1, y, z1, x1, y, z0);
    };
    const consumed = new Uint8Array(nx * nz);
    for (let iz = 0; iz < nz; iz++) {
      for (let ix = 0; ix < nx; ix++) {
        if (!filled[iz * nx + ix] || consumed[iz * nx + ix]) continue;
        let w = 1;
        while (ix + w < nx && filled[iz * nx + ix + w] && !consumed[iz * nx + ix + w]) w++;
        let h = 1;
        outer: while (iz + h < nz) {
          for (let k = 0; k < w; k++) {
            const j = (iz + h) * nx + ix + k;
            if (!filled[j] || consumed[j]) break outer;
          }
          h++;
        }
        for (let dz = 0; dz < h; dz++) {
          for (let k = 0; k < w; k++) consumed[(iz + dz) * nx + ix + k] = 1;
        }
        quad(
          minX + ix * step,
          minZ + iz * step,
          minX + (ix + w) * step,
          minZ + (iz + h) * step,
        );
      }
    }
    if (pos.length === 0) return null;
    const geom = new THREE.BufferGeometry();
    geom.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
    const normals = new Float32Array(pos.length);
    for (let i = 1; i < normals.length; i += 3) normals[i] = 1; // all +Y
    geom.setAttribute("normal", new THREE.BufferAttribute(normals, 3));
    return geom;
  }

  // Hide/show all walls. Hidden walls stop being pickable / snappable, and the
  // flat ground projection shows instead so their location stays visible.
  setVisible(v: boolean): void {
    this.group.visible = v;
    this.projGroup.visible = !v;
  }
  isVisible(): boolean {
    return this.group.visible;
  }
  // See-through walls: hand the camera + room-footprint polygon to occMat, whose
  // fragment shader fades each wall PIXEL standing between the viewer and the room
  // interior (floor, objects, or a far wall — see occRayHitsRoom). Only walls that
  // cover some interior get occMat (which can't write depth, so the interior shows
  // through); fully-opaque walls keep the depth-writing `mat` so they still
  // occlude the background grid dots behind them.
  updateOcclusionFade(camX: number, camY: number, camZ: number): void {
    // The footprint (uFoot) and wall height are in the wall group's LOCAL space,
    // so the whole test runs there. When the group is shown transformed (the AR
    // tabletop scales/positions/rotates the layout onto the desk) world ≠ local:
    // map the incoming world camera into local and hand the shader the matching
    // world→local matrix so its fragments land in the same space. Identity in the
    // desktop views, where the group sits at the world origin (1:1, no offset).
    this.group.updateWorldMatrix(true, false);
    _occInv.copy(this.group.matrixWorld).invert();
    this.occUniforms.uOccToLocal.value.copy(_occInv);
    _occCam.set(camX, camY, camZ).applyMatrix4(_occInv);
    camX = _occCam.x;
    camY = _occCam.y;
    camZ = _occCam.z;
    this.occUniforms.uCam.value.set(camX, camY, camZ);
    // Corner degree comes from the maintained incidence map — this runs every
    // 3D-orbit frame, and building a fresh degree Map over all walls here was
    // a per-frame allocation for information addWall/removeWall already track.
    let n = 0;
    for (const [, w] of this.walls) {
      if (n >= OCC_MAX_EDGES) break;
      if (!this.isCorner(w.a) || !this.isCorner(w.b)) continue;
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      this.occFootprint[n].set(a.x, a.z, b.x, b.z);
      n++;
    }
    this.occUniforms.uFootCount.value = n;
    this.occUniforms.uWallH.value = this.wallHeight;
    // Is the world XZ point inside the footprint? Parity ray-cast (+X) over the
    // edges built above — the CPU mirror of the shader's occInsideFoot.
    const insideFoot = (px: number, pz: number): boolean => {
      let crossings = 0;
      for (let i = 0; i < n; i++) {
        const e = this.occFootprint[i]; // edge (e.x,e.y)→(e.z,e.w) in XZ
        if (e.y > pz === e.w > pz) continue;
        const xc = e.x + ((pz - e.y) / (e.w - e.y)) * (e.z - e.x);
        if (xc > px) crossings++;
      }
      return (crossings & 1) === 1;
    };
    // Does any sight-line through this wall pass into the room volume beyond it?
    // (Same ray/prism test as occRayHitsRoom in the shader; this CPU pass only
    // decides which walls get the see-through material, so it samples a grid of
    // points over the wall and is a conservative superset of the per-pixel fade.)
    const rayHitsRoom = (px: number, py: number, pz: number): boolean => {
      if (n <= 0) return false;
      const dy = py - camY;
      let tLo: number, tHi: number;
      if (Math.abs(dy) > 1e-4) {
        const t0 = (0 - camY) / dy;
        const tH = (this.wallHeight - camY) / dy;
        tLo = Math.min(t0, tH);
        tHi = Math.max(t0, tH);
      } else {
        tLo = 1;
        tHi = 201;
      }
      tLo = Math.max(tLo, 1.0001);
      if (tLo >= tHi) return false;
      const ax = camX + tLo * (px - camX);
      const az = camZ + tLo * (pz - camZ);
      const bx = camX + tHi * (px - camX);
      const bz = camZ + tHi * (pz - camZ);
      if (insideFoot(ax, az) || insideFoot(bx, bz)) return true;
      for (let i = 0; i < n; i++) {
        const e = this.occFootprint[i];
        if (segHit(ax, az, bx, bz, e.x, e.y, e.z, e.w)) return true;
      }
      return false;
    };
    const occludes = (a: { x: number; z: number }, b: { x: number; z: number }): boolean => {
      for (let ui = 0; ui <= 4; ui++) {
        const u = ui / 4;
        const wx = a.x + (b.x - a.x) * u;
        const wz = a.z + (b.z - a.z) * u;
        for (const hf of [0.05, 0.4, 0.8]) {
          if (rayHitsRoom(wx, this.wallHeight * hf, wz)) return true;
        }
      }
      return false;
    };
    // Is a SOLID stretch of wall between the camera and the leaf centre (dx,dz,dy)?
    // Door leaves swing off the room boundary, so occludes()/occRayHitsRoom (which
    // classify a boundary surface) can't decide their fade — instead just ask
    // whether the sight-line to the leaf passes THROUGH a wall (→ fade) or reaches
    // it through an opening / from inside the room (→ stay solid). A crossing that
    // lands within an opening's along-wall span, above the wall top, or below the
    // floor is not solid and doesn't count.
    const wallBetween = (dx: number, dz: number, dy: number): boolean => {
      for (const w2 of this.walls.values()) {
        const a = this.verts.get(w2.a);
        const b = this.verts.get(w2.b);
        if (!a || !b) continue;
        const rx = b.x - a.x; // wall a→b
        const rz = b.z - a.z;
        const sx = dx - camX; // sight-line cam→leaf
        const sz = dz - camZ;
        const denom = rx * sz - rz * sx;
        if (Math.abs(denom) < 1e-9) continue; // parallel — no single crossing
        const cmx = camX - a.x;
        const cmz = camZ - a.z;
        const t = (cmx * sz - cmz * sx) / denom; // param along the wall
        const u = (cmx * rz - cmz * rx) / denom; // param along the sight-line
        if (t < 0 || t > 1 || u < 0 || u > 1) continue; // crossing off either span
        const hy = camY + u * (dy - camY); // height where the sight-line crosses
        if (hy < 0 || hy > this.wallHeight) continue; // under the floor / over the top
        const p = t * Math.hypot(rx, rz); // along-wall distance (m) from vertex a
        let inGap = false;
        for (const o of w2.openings) {
          if (p > o.offset - o.width / 2 && p < o.offset + o.width / 2) {
            inGap = true;
            break;
          }
        }
        if (!inGap) return true; // crossed a solid wall stretch
      }
      return false;
    };
    for (const [id, w] of this.walls) {
      const solid = id === this.selected || this.highlighted.has(id);
      let m = solid ? this.selMat : this.mat;
      if (!solid && occludes(this.verts.get(w.a)!, this.verts.get(w.b)!)) m = this.occMat;
      for (const p of w.pieces) p.material = m;
      // Only see-through (occMat) walls need the depth pre-pass; opaque ones
      // already write depth themselves.
      const seeThru = m === this.occMat;
      for (const p of w.depthPieces) p.visible = seeThru;
      // Fade the openings' own meshes so no opaque slab / bright pane floats in the
      // gap of a transparent wall. Window panes sit in the wall plane, so they fade
      // per-pixel with the wall (occGlassMat, gated on this wall being see-through).
      // Door leaves swing into the room, so each is decided on its own: it fades
      // (flat) only when a solid wall stands between it and the camera — see
      // wallBetween — independent of whether THIS wall is see-through, since the
      // leaf may be hidden by a different (parallel) wall.
      for (const leaf of w.leaves) {
        const fade = wallBetween(leaf.position.x, leaf.position.z, leaf.position.y);
        leaf.material = fade ? this.doorLeafFadeMat : this.doorLeafMat;
        for (const c of leaf.children) {
          (c as THREE.LineSegments).material = fade
            ? this.doorLeafBorderFadeMat
            : this.doorLeafBorderMat;
        }
      }
      for (const p of w.panes) p.material = seeThru ? this.occGlassMat : this.glassMat;
    }
  }
  // Reset all walls to solid (used outside the 3D orbit, where there's no
  // occlusion to see through).
  clearOcclusionFade(): void {
    this.occUniforms.uFootCount.value = 0; // → opening borders stay fully solid
    for (const [id, w] of this.walls) {
      const m = id === this.selected || this.highlighted.has(id) ? this.selMat : this.mat;
      for (const p of w.pieces) p.material = m;
      for (const p of w.depthPieces) p.visible = false;
      for (const leaf of w.leaves) {
        leaf.material = this.doorLeafMat;
        for (const c of leaf.children) {
          (c as THREE.LineSegments).material = this.doorLeafBorderMat;
        }
      }
      for (const p of w.panes) p.material = this.glassMat;
    }
  }

  // Layout-wide wall height (metres). setHeight re-lays every wall at the new
  // height; openings keep their sill/height (clamped to the new wall height by
  // the layout). The corner markers re-render at the new top in the next frame.
  height(): number {
    return this.wallHeight;
  }
  setHeight(m: number): void {
    const h = Math.max(0.1, m);
    if (h === this.wallHeight) return;
    markFloorMutation();
    this.wallHeight = h;
    for (const id of this.walls.keys()) this.layout(id);
    this.refreshLockMarkers(this.lockMarkersShowAll); // markers sit at the new top
  }

  // The two face lines (surfaces) of every wall: the centerline offset by
  // ±half-thickness, each carrying its OUTWARD normal (pointing away from the
  // centerline) so callers can snap to the near face only — never the far face
  // or the centerline. NOT gated on visibility: objects/measure can still snap
  // to a hidden wall's faces, using its ground projection as the reference.
  faceSegments(): {
    ax: number;
    az: number;
    bx: number;
    bz: number;
    nx: number;
    nz: number;
  }[] {
    const out: {
      ax: number;
      az: number;
      bx: number;
      bz: number;
      nx: number;
      nz: number;
    }[] = [];
    const h = WALL_THICKNESS / 2;
    for (const w of this.walls.values()) {
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz);
      if (len < 1e-6) continue;
      const nx = -dz / len; // left normal
      const nz = dx / len;
      for (const s of [h, -h]) {
        const sgn = s > 0 ? 1 : -1; // outward normal points from centerline to face
        out.push({
          ax: a.x + nx * s,
          az: a.z + nz * s,
          bx: b.x + nx * s,
          bz: b.z + nz * s,
          nx: nx * sgn,
          nz: nz * sgn,
        });
      }
    }
    return out;
  }
  moveVertex(id: number, x: number, z: number): void {
    const v = this.verts.get(id);
    if (!v) return;
    v.x = x;
    v.z = z;
    this.relayoutAround(id);
    // Reshaping a corner can flip which face is the inner one.
    this.autoOrientDimSides();
    this.refreshHandles();
  }

  // Translate a whole wall by (dx,dz): move BOTH its endpoints, so any walls
  // sharing those corners stretch/rotate to follow (drag-to-move a room wall).
  // Locked corners stay pinned — a wall with one locked end pivots around it.
  translateWall(id: number, dx: number, dz: number): void {
    const w = this.walls.get(id);
    if (!w) return;
    let moved = false;
    for (const vid of [w.a, w.b]) {
      if (this.lockedVerts.has(vid)) continue;
      const v = this.verts.get(vid)!;
      v.x += dx;
      v.z += dz;
      this.relayoutAround(vid);
      moved = true;
    }
    if (moved) {
      this.autoOrientDimSides();
      this.refreshHandles();
    }
  }

  // Snap a dragged vertex so its incident walls line up with the world axes
  // (horizontal / vertical) when they're within `angleRad` of one — so corners
  // square up to right angles as you drag. A near-horizontal wall pins the
  // vertex's z to its far end; near-vertical pins x. Both can apply at once
  // (a right-angle corner → exactly square). Returns the adjusted position.
  snapVertexAxis(vid: number, x: number, z: number, angleRad: number): { x: number; z: number } {
    const tan = Math.tan(angleRad);
    let sx = x;
    let sz = z;
    for (const w of this.walls.values()) {
      const far = w.a === vid ? this.verts.get(w.b) : w.b === vid ? this.verts.get(w.a) : null;
      if (!far) continue;
      const dx = x - far.x;
      const dz = z - far.z;
      const adx = Math.abs(dx);
      const adz = Math.abs(dz);
      if (adx < 1e-6 && adz < 1e-6) continue;
      if (adz <= adx * tan) sz = far.z; // near-horizontal → level it
      else if (adx <= adz * tan) sx = far.x; // near-vertical → plumb it
    }
    return { x: sx, z: sz };
  }

  // ── Corner locks ─────────────────────────────────────────────────────────
  // Pin specific corners (vertices) so edits route around them: a locked corner
  // can't be dragged, and a length edit moves the wall's OTHER (unlocked) end.
  private lockedVerts = new Set<number>();

  isVertexLocked(vid: number): boolean {
    return this.lockedVerts.has(vid);
  }
  toggleVertexLock(vid: number): void {
    markFloorMutation();
    if (this.lockedVerts.has(vid)) this.lockedVerts.delete(vid);
    else this.lockedVerts.add(vid);
    this.refreshLockMarkers(this.lockMarkersShowAll);
    this.refreshHandles(); // keep a selected wall's endpoint colours current
  }

  // Pick which end a length edit moves: the unlocked one (anchor the locked).
  // null = both locked (length is fixed). Defaults to moving b.
  private lengthEnds(w: WallEntry): { anchor: number; mover: number } | null {
    const aL = this.lockedVerts.has(w.a);
    const bL = this.lockedVerts.has(w.b);
    if (aL && bL) return null;
    if (bL) return { anchor: w.b, mover: w.a };
    return { anchor: w.a, mover: w.b };
  }

  // ── Lock markers (a clickable dot at every vertex, for the Lock tool) ──────
  readonly lockGroup = new THREE.Group();
  // Smaller + smoother than before, matching the measurement vertex dots.
  private lockGeo = new THREE.SphereGeometry(CORNER_R, 28, 20);
  // transparent (opacity 1) + no depth write → drawn in the transparent pass on
  // top of the see-through grid/dimension lines (see handleMat above).
  private lockMatOn = new THREE.MeshBasicMaterial({
    color: 0xff5a5a,
    depthTest: false,
    depthWrite: false,
    transparent: true,
  });
  private lockMatOff = new THREE.MeshBasicMaterial({
    color: 0xffd23f, // yellow — matches the measurement vertex dots (red when locked)
    depthTest: false,
    depthWrite: false,
    transparent: true,
  });
  private lockMarkers: THREE.Mesh[] = [];
  private lockMarkersShowAll = false;

  // Show a marker at each vertex (showAll = the Lock tool, all clickable) or at
  // only the locked ones (other tools, just so pinned corners are visible).
  // Hide every corner marker (Select tool when not hovering/selecting — clicking
  // away leaves the corners hidden whether locked or not).
  hideLockMarkers(): void {
    for (const m of this.lockMarkers) m.visible = false;
  }
  // Scale every corner marker + endpoint handle so they hold a constant on-
  // screen size as the user zooms (the caller passes the per-frame factor),
  // instead of ballooning with the world like the rest of the geometry.
  setMarkerScale(s: number): void {
    for (const m of this.lockMarkers) m.scale.setScalar(s);
    for (const h of this.handles) h.scale.setScalar(s);
  }
  refreshLockMarkers(showAll: boolean): void {
    this.lockMarkersShowAll = showAll;
    const verts = [...this.verts.entries()];
    while (this.lockMarkers.length < verts.length) {
      const m = new THREE.Mesh(this.lockGeo, this.lockMatOff);
      m.renderOrder = 20;
      this.lockMarkers.push(m);
      this.lockGroup.add(m);
    }
    let n = 0;
    for (const [vid, v] of verts) {
      const locked = this.lockedVerts.has(vid);
      if (!showAll && !locked) continue;
      const m = this.lockMarkers[n++];
      m.visible = true;
      m.position.set(v.x, this.wallHeight, v.z);
      m.material = locked ? this.lockMatOn : this.lockMatOff;
      m.userData.vid = vid;
    }
    for (let i = n; i < this.lockMarkers.length; i++) this.lockMarkers[i].visible = false;
  }

  // Closest approach between a cursor ray and this wall's axis line (a horizontal
  // line through the wall at mid-height). Returns the param `s` along the wall
  // (distance from vertex a) and the ray's closest point. View-INDEPENDENT — it
  // works whether you look top-down or head-on, unlike intersecting one fixed
  // plane (a horizontal plane degenerates head-on; a vertical one degenerates
  // top-down).
  private rayAxisClosest(
    w: WallEntry,
    origin: THREE.Vector3,
    dir: THREE.Vector3,
  ): { s: number; cx: number; cy: number; cz: number } | null {
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    const L = Math.hypot(b.x - a.x, b.z - a.z);
    if (L < 1e-6) return null;
    const ex = (b.x - a.x) / L; // wall axis (unit, horizontal)
    const ez = (b.z - a.z) / L;
    const dl = Math.hypot(dir.x, dir.y, dir.z) || 1;
    const ux = dir.x / dl;
    const uy = dir.y / dl;
    const uz = dir.z / dl;
    const bb = ex * ux + ez * uz; // axis · rayDir (axis has no y)
    const denom = 1 - bb * bb;
    if (Math.abs(denom) < 1e-6) return null; // looking along the wall — ambiguous
    const hy = this.wallHeight * 0.5;
    const rx = a.x - origin.x;
    const ry = hy - origin.y;
    const rz = a.z - origin.z;
    const d1r = ex * rx + ez * rz; // axis · (P_axis − P_ray)
    const d2r = ux * rx + uy * ry + uz * rz; // rayDir · (P_axis − P_ray)
    const s = (bb * d2r - d1r) / denom; // param along the wall
    const tRay = (d2r - bb * d1r) / denom; // param along the ray
    return {
      s,
      cx: origin.x + ux * tRay,
      cy: origin.y + uy * tRay,
      cz: origin.z + uz * tRay,
    };
  }
  // The opening on `wallId` the cursor ray points at, or null — the whole door/
  // window width is a grab target (it's a gap with no wall mesh, so a click there
  // would otherwise fall through to whatever is behind it). The handle dot is just
  // the precise center grip.
  openingAtRay(
    wallId: number,
    origin: THREE.Vector3,
    dir: THREE.Vector3,
  ): number | null {
    const w = this.walls.get(wallId);
    return w ? (this.openingHit(w, origin, dir)?.openingId ?? null) : null;
  }
  // The opening on a wall the ray points at, with the ray distance to its wall-
  // plane crossing — so callers can depth-compare it against placements (a thing
  // IN FRONT of the doorway still wins; one BEHIND it is blocked by the door).
  private openingHit(
    w: WallEntry,
    origin: THREE.Vector3,
    dir: THREE.Vector3,
  ): { openingId: number; distance: number } | null {
    const cl = this.rayAxisClosest(w, origin, dir);
    if (!cl) return null;
    // The ray's closest point must land on the wall face: within its height and
    // not far off the wall plane (in XZ).
    if (cl.cy < -0.2 || cl.cy > this.wallHeight + 0.2) return null;
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    const L = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    const ex = (b.x - a.x) / L;
    const ez = (b.z - a.z) / L;
    const perp = Math.abs((cl.cx - a.x) * -ez + (cl.cz - a.z) * ex);
    if (perp > WALL_THICKNESS * 3) return null;
    for (const o of w.openings) {
      if (cl.s >= o.offset - o.width / 2 && cl.s <= o.offset + o.width / 2) {
        const distance = Math.hypot(cl.cx - origin.x, cl.cy - origin.y, cl.cz - origin.z);
        return { openingId: o.id, distance };
      }
    }
    return null;
  }
  // ── Opening editor (selected opening: border, resize, exact dims) ──────────
  private static OPEN_MIN = 0.2; // smallest opening width/height (metres)

  // Is this vertex a corner — shared with another wall?
  private isCorner(vid: number): boolean {
    return (this.incident.get(vid)?.size ?? 0) >= 2;
  }
  // The span [lo, hi] along the wall an opening may occupy. Pulled one wall
  // thickness in from any end that meets another wall, so the opening stops at
  // the adjacent wall's face rather than reaching the bare corner and exposing
  // that wall's interior. Free ends keep the full reach; collapses to the whole
  // wall if the margins would leave no usable room.
  private openingBounds(w: WallEntry): { lo: number; hi: number } {
    const L = this.wallLength(w);
    let lo = this.isCorner(w.a) ? WALL_THICKNESS : 0;
    let hi = L - (this.isCorner(w.b) ? WALL_THICKNESS : 0);
    if (hi - lo < WallSystem.OPEN_MIN) {
      lo = 0;
      hi = L;
    }
    return { lo, hi };
  }
  // Clamp an opening center so its full width stays inside the wall's usable span.
  private clampOffset(w: WallEntry, width: number, offset: number): number {
    const { lo, hi } = this.openingBounds(w);
    const half = width / 2;
    return Math.max(lo + half, Math.min(hi - half, offset));
  }

  // Look up an opening by id together with its wall — the lead-in to nearly
  // every opening method. Null if either the wall or the opening is gone.
  private findOpening(
    wallId: number,
    openingId: number,
  ): { w: WallEntry; o: Opening } | null {
    const w = this.walls.get(wallId);
    const o = w?.openings.find((x) => x.id === openingId);
    return w && o ? { w, o } : null;
  }

  openingProps(wallId: number, openingId: number): Opening | null {
    const o = this.findOpening(wallId, openingId)?.o;
    return o ? { ...o } : null;
  }
  // The clear gap on each side of an opening, as params along the wall (distance
  // from vertex a): from the opening's left edge back to the nearest neighbour's
  // right edge (or the wall start), and from its right edge to the next
  // neighbour's left edge (or the wall end). `originX/Z` is vertex a.
  openingGaps(
    wallId: number,
    openingId: number,
  ): { originX: number; originZ: number; leftStart: number; leftEnd: number; rightStart: number; rightEnd: number } | null {
    const f = this.findOpening(wallId, openingId);
    if (!f) return null;
    const { w, o } = f;
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    const L = Math.hypot(b.x - a.x, b.z - a.z);
    if (L < 1e-6) return null;
    const left = o.offset - o.width / 2;
    const right = o.offset + o.width / 2;
    let leftStart = 0;
    let rightEnd = L;
    for (const x of w.openings) {
      if (x.id === openingId) continue;
      const xl = x.offset - x.width / 2;
      const xr = x.offset + x.width / 2;
      if (xr <= left + 1e-6) leftStart = Math.max(leftStart, xr); // neighbour to the left
      if (xl >= right - 1e-6) rightEnd = Math.min(rightEnd, xl); // neighbour to the right
    }
    return { originX: a.x, originZ: a.z, leftStart, leftEnd: left, rightStart: right, rightEnd };
  }
  // World placement of an opening's rectangle on the wall, for drawing the border
  // + resize handles: the rect center along the wall, the wall's unit direction,
  // half-width, and the sill/top heights.
  openingGeometry(
    wallId: number,
    openingId: number,
  ): { cx: number; cz: number; ux: number; uz: number; halfW: number; sill: number; top: number; width: number; height: number; kind: OpeningKind } | null {
    const f = this.findOpening(wallId, openingId);
    if (!f) return null;
    const { w, o } = f;
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    const L = Math.hypot(b.x - a.x, b.z - a.z);
    if (L < 1e-6) return null;
    const ux = (b.x - a.x) / L;
    const uz = (b.z - a.z) / L;
    return {
      cx: a.x + ux * o.offset,
      cz: a.z + uz * o.offset,
      ux,
      uz,
      halfW: o.width / 2,
      sill: o.sill,
      top: o.sill + o.height,
      width: o.width,
      height: o.height,
      kind: o.kind,
    };
  }
  // Set an exact width or height (metres), clamped to fit the wall.
  setOpeningDim(wallId: number, openingId: number, dim: "width" | "height", value: number): void {
    const f = this.findOpening(wallId, openingId);
    if (!f) return;
    const { w, o } = f;
    if (dim === "width") {
      const { lo, hi } = this.openingBounds(w);
      if (o.lockLeft && o.lockRight) return; // both jambs pinned → width is fixed
      const newW = Math.max(WallSystem.OPEN_MIN, Math.min(hi - lo, value));
      if (o.lockLeft) {
        // Grow/shrink from the right, keeping the left jamb in place.
        const left = o.offset - o.width / 2;
        o.width = Math.min(newW, hi - left);
        o.offset = left + o.width / 2;
      } else if (o.lockRight) {
        const right = o.offset + o.width / 2;
        o.width = Math.min(newW, right - lo);
        o.offset = right - o.width / 2;
      } else {
        o.width = newW;
        o.offset = Math.max(lo + o.width / 2, Math.min(hi - o.width / 2, o.offset));
      }
    } else {
      if (o.lockTop && o.lockBottom) return; // both ends pinned → height is fixed
      const newH = Math.max(WallSystem.OPEN_MIN, value);
      if (o.lockTop) {
        // Keep the head fixed; grow/shrink downward (sill can't drop below 0).
        const top = o.sill + o.height;
        o.height = Math.min(newH, top);
        o.sill = top - o.height;
      } else {
        // Keep the sill fixed; grow/shrink the head up to the ceiling.
        o.height = Math.min(newH, this.wallHeight - o.sill);
      }
    }
    this.layout(wallId);
  }
  // Drag one edge of an opening to the cursor ray, keeping the opposite edge
  // fixed. left/right resize the width (along the wall, any view); top/bottom
  // resize the height (needs the wall's vertical plane, so 3D / head-on).
  resizeOpeningEdgeByRay(
    wallId: number,
    openingId: number,
    edge: "left" | "right" | "top" | "bottom",
    origin: THREE.Vector3,
    dir: THREE.Vector3,
  ): void {
    const f = this.findOpening(wallId, openingId);
    if (!f) return;
    const { w, o } = f;
    const MIN = WallSystem.OPEN_MIN;
    if (edge === "left" || edge === "right") {
      if (edge === "left" ? o.lockLeft : o.lockRight) return; // pinned jamb
      const cl = this.rayAxisClosest(w, origin, dir);
      if (!cl) return;
      const { lo, hi } = this.openingBounds(w);
      const left = o.offset - o.width / 2;
      const right = o.offset + o.width / 2;
      if (edge === "right") {
        const nr = Math.max(left + MIN, Math.min(hi, cl.s));
        o.width = nr - left;
        o.offset = (left + nr) / 2;
      } else {
        const nl = Math.max(lo, Math.min(right - MIN, cl.s));
        o.width = right - nl;
        o.offset = (nl + right) / 2;
      }
    } else {
      if (edge === "top" ? o.lockTop : o.lockBottom) return; // pinned edge
      const y = this.rayWallPlaneY(w, origin, dir);
      if (y === null) return;
      const top = o.sill + o.height;
      if (edge === "top") {
        const nt = Math.max(o.sill + MIN, Math.min(this.wallHeight, y));
        o.height = nt - o.sill;
      } else {
        const ns = Math.max(0, Math.min(top - MIN, y));
        o.sill = ns;
        o.height = top - ns;
      }
    }
    this.layout(wallId);
  }
  // Where the cursor ray crosses the wall's vertical plane (through its axis),
  // decomposed into the param `s` along the wall and the height `y`. Null if the
  // ray is ~parallel (e.g. a top-down view).
  private rayWallPlaneHit(
    w: WallEntry,
    origin: THREE.Vector3,
    dir: THREE.Vector3,
  ): { s: number; y: number } | null {
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    const L = Math.hypot(b.x - a.x, b.z - a.z) || 1;
    const ux = (b.x - a.x) / L;
    const uz = (b.z - a.z) / L;
    const nx = -uz; // wall XZ normal
    const nz = ux;
    const dn = dir.x * nx + dir.z * nz;
    if (Math.abs(dn) < 1e-6) return null;
    const t = -(nx * (origin.x - a.x) + nz * (origin.z - a.z)) / dn;
    if (t < 0) return null;
    const hx = origin.x + dir.x * t;
    const hz = origin.z + dir.z * t;
    return { s: (hx - a.x) * ux + (hz - a.z) * uz, y: origin.y + dir.y * t };
  }
  private rayWallPlaneY(w: WallEntry, origin: THREE.Vector3, dir: THREE.Vector3): number | null {
    return this.rayWallPlaneHit(w, origin, dir)?.y ?? null;
  }
  // Move an opening toward the cursor ray: along the wall, and up/down too (both
  // doors and windows can be raised off the floor). Used by the move (orange)
  // handle. A locked jamb / head / sill pins that axis.
  moveOpeningByRay(
    wallId: number,
    openingId: number,
    origin: THREE.Vector3,
    dir: THREE.Vector3,
  ): void {
    const f = this.findOpening(wallId, openingId);
    if (!f) return;
    const { w, o } = f;
    const pinnedH = o.lockLeft || o.lockRight; // a locked jamb anchors the slide
    const pinnedV = o.lockTop || o.lockBottom; // a locked head/sill anchors the rise
    const hit = this.rayWallPlaneHit(w, origin, dir);
    if (hit) {
      if (!pinnedH) o.offset = this.clampOffset(w, o.width, hit.s);
      if (!pinnedV) o.sill = Math.max(0, Math.min(this.wallHeight - o.height, hit.y - o.height / 2));
    } else if (!pinnedH) {
      const cl = this.rayAxisClosest(w, origin, dir);
      if (cl) o.offset = this.clampOffset(w, o.width, cl.s);
    }
    this.layout(wallId);
  }
  // Direct position setters (from editing the gap pills), clamped to the wall.
  setOpeningOffset(wallId: number, openingId: number, offset: number): void {
    const f = this.findOpening(wallId, openingId);
    if (!f) return;
    f.o.offset = this.clampOffset(f.w, f.o.width, offset);
    this.layout(wallId);
  }
  // Set the floor→sill gap by moving the SILL while keeping the head fixed —
  // like dragging the bottom handle. No-op if the sill (bottom) is pinned.
  setOpeningSillGap(wallId: number, openingId: number, gap: number): void {
    const f = this.findOpening(wallId, openingId);
    if (!f || f.o.lockBottom) return;
    const { o } = f;
    const top = o.sill + o.height;
    const ns = Math.max(0, Math.min(top - WallSystem.OPEN_MIN, gap));
    o.sill = ns;
    o.height = top - ns;
    this.layout(wallId);
  }
  // Set the head→wall-top gap by moving the HEAD while keeping the sill fixed —
  // like dragging the top handle. No-op if the head (top) is pinned.
  setOpeningHeadGap(wallId: number, openingId: number, gap: number): void {
    const f = this.findOpening(wallId, openingId);
    if (!f || f.o.lockTop) return;
    const { o } = f;
    const newTop = Math.max(
      o.sill + WallSystem.OPEN_MIN,
      Math.min(this.wallHeight, this.wallHeight - gap),
    );
    o.height = newTop - o.sill;
    this.layout(wallId);
  }
  // Pin/unpin an edge of an opening: a locked edge can't be dragged and size/
  // move edits keep it fixed — like a locked wall corner. Left/right pin the
  // jambs along the wall; top/bottom pin the head/sill heights.
  isOpeningEdgeLocked(wallId: number, openingId: number, edge: OpeningEdge): boolean {
    const o = this.findOpening(wallId, openingId)?.o;
    if (!o) return false;
    return !!(edge === "left"
      ? o.lockLeft
      : edge === "right"
        ? o.lockRight
        : edge === "top"
          ? o.lockTop
          : o.lockBottom);
  }
  toggleOpeningEdgeLock(wallId: number, openingId: number, edge: OpeningEdge): void {
    const o = this.findOpening(wallId, openingId)?.o;
    if (!o) return;
    markFloorMutation();
    if (edge === "left") o.lockLeft = !o.lockLeft;
    else if (edge === "right") o.lockRight = !o.lockRight;
    else if (edge === "top") o.lockTop = !o.lockTop;
    else o.lockBottom = !o.lockBottom;
  }

  // ── Walls ───────────────────────────────────────────────────────────────
  addWall(ax: number, az: number, bx: number, bz: number): number {
    const a = this.vertexNear(ax, az, 1e-4) ?? this.addVertex(ax, az);
    const b = this.vertexNear(bx, bz, 1e-4) ?? this.addVertex(bx, bz);
    const id = this.nextW++;
    const group = new THREE.Group();
    group.userData.wallId = id;
    this.group.add(group);
    this.walls.set(id, {
      a, b, group, pieces: [], depthPieces: [], panes: [], leaves: [], swings: [], openings: [], dimSide: 0, dimSideExplicit: false,
    });
    this.linkIncident(a, id);
    this.linkIncident(b, id);
    // Re-lay both ends so existing walls miter against the new one.
    this.relayoutAround(a);
    this.relayoutAround(b);
    // A fresh connection may turn standalone walls into a corner/room — give
    // every non-overridden wall its inner face.
    this.autoOrientDimSides();
    return id;
  }
  removeWall(id: number): void {
    const w = this.walls.get(id);
    if (!w) return;
    markFloorMutation();
    this.group.remove(w.group);
    if (w.proj) {
      this.projGroup.remove(w.proj);
      w.proj.geometry.dispose();
    }
    w.border?.geometry.dispose();
    this.walls.delete(id);
    this.labelCache = null;
    this.unlinkIncident(w.a, id);
    this.unlinkIncident(w.b, id);
    for (const vid of [w.a, w.b]) {
      if (!this.incident.get(vid)?.size) this.verts.delete(vid);
    }
    // Re-miter walls that were joined to the removed one.
    this.ceilingDirty = true; // an isolated wall's removal reaches no layout()
    this.seeThruCeilingDirty = true;
    this.relayoutAround(w.a);
    this.relayoutAround(w.b);
    // Losing a neighbour can drop a wall back to standalone (→ centerline).
    this.autoOrientDimSides();
    if (this.selected === id) this.setSelected(null);
  }

  private wallLength(w: WallEntry): number {
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    return Math.hypot(b.x - a.x, b.z - a.z);
  }

  // Re-lay every wall touching a vertex — their mitered corners depend on
  // their neighbours, so a change at a shared vertex affects them all.
  private relayoutAround(vid: number): void {
    const inc = this.incident.get(vid);
    if (inc) for (const wid of inc) this.layout(wid);
  }

  // vertexId → ids of walls on it. Kept in sync by addWall/removeWall (and
  // clearAll); loadWalls funnels through those. Turns the per-endpoint
  // neighbour scans (miterEnds runs twice per layout()) from O(walls) into
  // O(degree), and gives the occlusion pass its corner-degree test for free.
  private incident = new Map<number, Set<number>>();
  private linkIncident(vid: number, wid: number): void {
    let s = this.incident.get(vid);
    if (!s) this.incident.set(vid, (s = new Set()));
    s.add(wid);
  }
  private unlinkIncident(vid: number, wid: number): void {
    const s = this.incident.get(vid);
    if (!s) return;
    s.delete(wid);
    if (s.size === 0) this.incident.delete(vid);
  }

  // Intersect line (p,d) with line (q,e); null if ~parallel or the hit is past
  // the miter limit (acute spike → caller falls back to a square cap).
  private lineHit(
    px: number, pz: number, dx: number, dz: number,
    qx: number, qz: number, ex: number, ez: number,
  ): P2 | null {
    const denom = dx * ez - dz * ex;
    if (Math.abs(denom) < 1e-9) return null;
    const s = ((qx - px) * ez - (qz - pz) * ex) / denom;
    if (Math.abs(s) > MITER_LIMIT) return null;
    return { x: px + dx * s, z: pz + dz * s };
  }

  // Left/right footprint corners of `wallId` at vertex V, mitered against the
  // angularly-adjacent walls there so joints meet instead of overlapping. No
  // neighbour on a side → a square (perpendicular) cap.
  private miterEnds(V: number, wallId: number): { left: P2; right: P2 } {
    const v = this.verts.get(V)!;
    const inc: Inc[] = [];
    for (const wid of this.incident.get(V) ?? []) {
      const w = this.walls.get(wid)!;
      const o = this.verts.get(w.a === V ? w.b : w.a);
      if (!o) continue;
      const dx = o.x - v.x;
      const dz = o.z - v.z;
      const len = Math.hypot(dx, dz);
      if (len < 1e-6) continue;
      inc.push({ wid, ux: dx / len, uz: dz / len, ang: Math.atan2(dz, dx) });
    }
    const me = inc.find((i) => i.wid === wallId)!;
    const h = WALL_THICKNESS / 2;
    const nlx = -me.uz; // me's left normal
    const nlz = me.ux;
    // Defaults: a square cap (perpendicular faces at V).
    let left: P2 = { x: v.x + h * nlx, z: v.z + h * nlz };
    let right: P2 = { x: v.x - h * nlx, z: v.z - h * nlz };
    // Nearest neighbour going CCW and CW around V.
    let ccw: Inc | null = null;
    let cw: Inc | null = null;
    let ccwD = Infinity;
    let cwD = Infinity;
    for (const i of inc) {
      if (i.wid === wallId) continue;
      let d = i.ang - me.ang;
      while (d <= 1e-9) d += Math.PI * 2; // CCW gap in (0, 2π]
      if (d < ccwD) { ccwD = d; ccw = i; }
      const back = Math.PI * 2 - d; // CW gap
      if (back < cwD) { cwD = back; cw = i; }
    }
    if (ccw) {
      // me's LEFT face ∩ CCW neighbour's RIGHT face.
      const hit = this.lineHit(
        v.x + h * nlx, v.z + h * nlz, me.ux, me.uz,
        v.x + h * ccw.uz, v.z - h * ccw.ux, ccw.ux, ccw.uz,
      );
      if (hit) left = hit;
    }
    if (cw) {
      // me's RIGHT face ∩ CW neighbour's LEFT face.
      const hit = this.lineHit(
        v.x - h * nlx, v.z - h * nlz, me.ux, me.uz,
        v.x - h * cw.uz, v.z + h * cw.ux, cw.ux, cw.uz,
      );
      if (hit) right = hit;
    }
    return { left, right };
  }

  // The four mitered footprint corners of a wall (world XZ). miterEnds works in
  // each vertex's OUTGOING frame; at B that frame points B→A (reversed), so B's
  // left/right are swapped to match the wall's consistent left/right — without
  // this the footprint becomes a bow-tie that pinches at the middle.
  private wallCorners(id: number): { leftA: P2; rightA: P2; leftB: P2; rightB: P2 } {
    const w = this.walls.get(id)!;
    const A = this.miterEnds(w.a, id);
    const B = this.miterEnds(w.b, id);
    return { leftA: A.left, rightA: A.right, leftB: B.right, rightB: B.left };
  }

  // Rebuild a wall as extruded, corner-mitered footprint pieces (around its
  // openings) + glass panes — built in WORLD coords under an identity group.
  // Fast path: when the wall's structure (piece plan, openings, caps) matches
  // the previous build — the common case for every frame of a corner/body
  // drag — the freshly computed positions are written into the existing
  // geometry buffers instead of disposing and recreating every mesh.
  private layout(id: number): void {
    markFloorMutation(); // every geometry mutation funnels through here
    this.ceilingDirty = true; // any wall change can reshape the roof footprint
    this.seeThruCeilingDirty = true;
    this.labelCache = null; // pill positions/lengths follow the geometry
    const w = this.walls.get(id);
    if (!w) return;
    const plan = this.planWall(w, id);
    if (plan && planKeysEq(w.planKey, plan.key) && this.applyPlanInPlace(w, plan)) {
      return;
    }
    this.rebuildFromPlan(w, id, plan);
  }

  // Compute everything layout() will draw, as pure data. Null for a
  // degenerate (zero-length) wall.
  private planWall(w: WallEntry, id: number): WallPlan | null {
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const L = Math.hypot(dx, dz);
    if (L < 1e-6) return null;

    const ux = dx / L;
    const uz = dz / L;
    const nlx = -uz; // left normal
    const nlz = ux;
    const h = WALL_THICKNESS / 2;
    const eps = 1e-4;
    const c = this.wallCorners(id);

    const plan: WallPlan = {
      key: [],
      // Flat ground footprint (one quad over the whole wall) for the
      // projection shown while walls are hidden.
      proj: [c.leftA, c.leftB, c.rightB, c.rightA],
      pieces: [],
      panes: [],
      leaves: [],
      swings: [],
      border: [],
    };

    // A structural piece spanning [x0,x1] along the centerline, raised [y0,y1].
    // The wall ends use the mitered corners; interior cuts are square.
    const addSeg = (x0: number, x1: number, y0: number, y1: number) => {
      if (x1 - x0 <= eps || y1 - y0 <= eps) return;
      const sx = a.x + ux * x0;
      const sz = a.z + uz * x0;
      const ex = a.x + ux * x1;
      const ez = a.z + uz * x1;
      const atStart = x0 <= eps; // segment's near edge is the wall start
      const atEnd = x1 >= L - eps; // segment's far edge is the wall end
      const sL = atStart ? c.leftA : { x: sx + h * nlx, z: sz + h * nlz };
      const sR = atStart ? c.rightA : { x: sx - h * nlx, z: sz - h * nlz };
      const eL = atEnd ? c.leftB : { x: ex + h * nlx, z: ez + h * nlz };
      const eR = atEnd ? c.rightB : { x: ex - h * nlx, z: ez - h * nlz };
      // Only wall-end edges get a thickness cap; interior seams are left open so
      // they don't show as a dark sliver through a see-through wall (the flush
      // neighbour covers them; opening jambs get a reveal quad instead).
      plan.pieces.push({
        kind: "prism",
        c0: sL, c1: eL, c2: eR, c3: sR,
        y0, y1,
        capStart: atStart, capEnd: atEnd,
      });
      plan.key.push(1 + (atStart ? 2 : 0) + (atEnd ? 4 : 0));
    };

    // A vertical reveal quad spanning the wall thickness at along-wall position
    // `p`, from y0 to y1 — the jamb face seen through an opening. It exists only
    // over the opening's height, so it never extends into the solid wall above
    // or below as a sliver. Registered like a structural piece (fades + depth
    // twin) so it stays consistent with the rest of the wall.
    const addReveal = (p: number, y0: number, y1: number) => {
      if (y1 - y0 <= eps) return;
      const px = a.x + ux * p;
      const pz = a.z + uz * p;
      const atStart = p <= eps;
      const atEnd = p >= L - eps;
      const PL = atStart ? c.leftA : atEnd ? c.leftB : { x: px + h * nlx, z: pz + h * nlz };
      const PR = atStart ? c.rightA : atEnd ? c.rightB : { x: px - h * nlx, z: pz - h * nlz };
      plan.pieces.push({ kind: "reveal", pl: PL, pr: PR, y0, y1 });
      plan.key.push(0);
    };

    const ops = [...w.openings]
      .map((o) => ({ ...o, c0: o.offset - o.width / 2, c1: o.offset + o.width / 2 }))
      .filter((o) => o.c1 > 0 && o.c0 < L)
      .sort((p, q) => p.c0 - q.c0);

    // Black outline traced around each opening on BOTH wall faces; doors skip the
    // bottom edge (they meet the floor). Lines sit a hair proud of the face to
    // avoid z-fighting with it.
    const seg = (
      x0: number, y0: number, z0: number,
      x1: number, y1: number, z1: number,
    ): void => {
      plan.border.push(x0, y0, z0, x1, y1, z1);
    };

    let cursor = 0;
    for (const o of ops) {
      const left = Math.max(0, o.c0);
      const right = Math.min(L, o.c1);
      if (left > cursor) addSeg(cursor, left, 0, this.wallHeight);
      const top = Math.min(o.sill + o.height, this.wallHeight);
      if (o.sill > 1e-3) addSeg(left, right, 0, o.sill); // under sill
      if (top < this.wallHeight - 1e-3) addSeg(left, right, top, this.wallHeight); // lintel
      // Jamb reveals: the thickness faces at the opening's two edges, spanning
      // only the opening height (the segments no longer emit these interior caps).
      addReveal(left, o.sill, top);
      addReveal(right, o.sill, top);
      if (o.kind === "window") {
        const mid = (left + right) / 2;
        plan.panes.push({
          sx: right - left, sy: top - o.sill, sz: WALL_THICKNESS * 0.3,
          px: a.x + ux * mid, py: (o.sill + top) / 2, pz: a.z + uz * mid,
          rotY: -Math.atan2(uz, ux),
        });
      } else {
        // Door: a leaf panel hung open 90° at one jamb, plus a quarter-circle
        // floor arc tracing its swing from closed to open. Hinge side + swing
        // side come from the opening's flags (defaults: left jamb, left-normal
        // face). Uses the door's true jamb edges (o.c0/o.c1), not the wall-
        // clamped span, so a leaf reads correctly even at a wall end.
        const wHalf = o.width / 2;
        const hingeP = o.hingeRight ? o.c1 : o.c0; // hinge jamb param along wall
        const hx = a.x + ux * hingeP;
        const hz = a.z + uz * hingeP;
        const sw = o.swingNeg ? -1 : 1; // which face the door opens onto
        const sdx = sw * nlx; // open (90°) leaf direction = swing normal
        const sdz = sw * nlz;
        plan.leaves.push({
          sx: o.width, sy: top - o.sill, sz: DOOR_LEAF_THICK,
          px: hx + sdx * wHalf, py: (o.sill + top) / 2, pz: hz + sdz * wHalf,
          rotY: -Math.atan2(sdz, sdx),
        });
        // Swing arc: sweep 90° from the closed (along-wall, toward the latch)
        // direction to the open (swing-normal) direction, radius = leaf width.
        const lx = o.hingeRight ? -ux : ux; // closed leaf points toward the latch
        const lz = o.hingeRight ? -uz : uz;
        const a0 = Math.atan2(lz, lx);
        let dA = Math.atan2(sdz, sdx) - a0;
        while (dA > Math.PI) dA -= 2 * Math.PI;
        while (dA < -Math.PI) dA += 2 * Math.PI;
        const N = 12;
        const arcY = PROJ_Y;
        const arcPts: number[] = [];
        let pax = hx + Math.cos(a0) * o.width;
        let paz = hz + Math.sin(a0) * o.width;
        for (let i = 1; i <= N; i++) {
          const ang = a0 + dA * (i / N);
          const nx = hx + Math.cos(ang) * o.width;
          const nz = hz + Math.sin(ang) * o.width;
          arcPts.push(pax, arcY, paz, nx, arcY, nz);
          pax = nx;
          paz = nz;
        }
        plan.swings.push(arcPts);
      }
      // Frame outline on each face (offset just proud so it isn't z-fought).
      const fo = h + 0.004;
      for (const s of [1, -1]) {
        const ox = nlx * fo * s;
        const oz = nlz * fo * s;
        const lx = a.x + ux * left + ox;
        const lz = a.z + uz * left + oz;
        const rx = a.x + ux * right + ox;
        const rz = a.z + uz * right + oz;
        seg(lx, o.sill, lz, lx, top, lz); // left jamb
        seg(rx, o.sill, rz, rx, top, rz); // right jamb
        seg(lx, top, lz, rx, top, rz); // head (top)
        // Sill (bottom) edge: windows always; a door only once raised off the floor.
        if (o.kind === "window" || o.sill > 1e-3) seg(lx, o.sill, lz, rx, o.sill, rz);
      }
      cursor = Math.max(cursor, right);
    }
    if (cursor < L) addSeg(cursor, L, 0, this.wallHeight);

    // Structural tail: counts whose change forces a rebuild.
    plan.key.push(-1, plan.panes.length, plan.leaves.length, plan.swings.length,
      plan.border.length);
    return plan;
  }

  // Write the plan's positions into the wall's existing meshes. Only valid
  // when the structural key matched; returns false (→ full rebuild) if any
  // count disagrees anyway.
  private applyPlanInPlace(w: WallEntry, plan: WallPlan): boolean {
    if (
      !w.proj ||
      w.pieces.length !== plan.pieces.length ||
      w.panes.length !== plan.panes.length ||
      w.leaves.length !== plan.leaves.length ||
      w.swings.length !== plan.swings.length ||
      (plan.border.length > 0) !== !!w.border
    ) {
      return false;
    }
    const [p0, p1, p2, p3] = plan.proj;
    writePositions(w.proj.geometry as THREE.BufferGeometry, [
      p0.x, PROJ_Y, p0.z, p1.x, PROJ_Y, p1.z, p2.x, PROJ_Y, p2.z, p3.x, PROJ_Y, p3.z,
    ]);
    for (let i = 0; i < plan.pieces.length; i++) {
      const d = plan.pieces[i];
      const geo = w.pieces[i].geometry as THREE.BufferGeometry;
      writePositions(
        geo,
        d.kind === "prism"
          ? prismPositions(d.c0, d.c1, d.c2, d.c3, d.y0, d.y1)
          : revealPositions(d.pl, d.pr, d.y0, d.y1),
      );
      geo.computeVertexNormals();
      (geo.getAttribute("normal") as THREE.BufferAttribute).needsUpdate = true;
    }
    const applyBox = (mesh: THREE.Mesh, bp: BoxPlan): void => {
      mesh.scale.set(bp.sx, bp.sy, bp.sz);
      mesh.position.set(bp.px, bp.py, bp.pz);
      mesh.rotation.y = bp.rotY;
    };
    for (let i = 0; i < plan.panes.length; i++) applyBox(w.panes[i], plan.panes[i]);
    for (let i = 0; i < plan.leaves.length; i++) applyBox(w.leaves[i], plan.leaves[i]);
    for (let i = 0; i < plan.swings.length; i++) {
      writePositions(w.swings[i].geometry as THREE.BufferGeometry, plan.swings[i]);
    }
    if (w.border) writePositions(w.border.geometry as THREE.BufferGeometry, plan.border);
    return true;
  }

  // Tear down and rebuild every mesh of the wall from the plan.
  private rebuildFromPlan(w: WallEntry, id: number, plan: WallPlan | null): void {
    for (const m of w.depthPieces) w.group.remove(m); // share geometry with pieces
    for (const m of w.pieces) {
      w.group.remove(m);
      (m.geometry as THREE.BufferGeometry).dispose();
    }
    for (const m of w.panes) w.group.remove(m);
    for (const m of w.leaves) w.group.remove(m); // shared geo (this.geo) — no dispose
    for (const m of w.swings) {
      w.group.remove(m);
      m.geometry.dispose();
    }
    w.pieces.length = 0;
    w.depthPieces.length = 0;
    w.panes.length = 0;
    w.leaves.length = 0;
    w.swings.length = 0;
    if (w.border) {
      w.group.remove(w.border);
      w.border.geometry.dispose();
      w.border = undefined;
    }
    w.group.position.set(0, 0, 0);
    w.group.rotation.set(0, 0, 0);
    if (w.proj) {
      this.projGroup.remove(w.proj);
      w.proj.geometry.dispose();
      w.proj = undefined;
    }
    w.planKey = undefined;
    if (!plan) return; // degenerate (zero-length) wall

    const pieceMat =
      id === this.selected || this.highlighted.has(id) ? this.selMat : this.mat;

    w.proj = new THREE.Mesh(
      flatQuad(plan.proj[0], plan.proj[1], plan.proj[2], plan.proj[3]),
      this.projMat,
    );
    w.proj.userData.wallId = id;
    this.projGroup.add(w.proj);

    // Structural pieces (prisms + jamb reveals) with their depth-only twins
    // (same geometry) for the see-through pre-pass; the twin is hidden by
    // default, shown only while this wall uses the transparent occMat.
    for (const d of plan.pieces) {
      let g: THREE.BufferGeometry;
      if (d.kind === "prism") {
        g = prismGeometry(d.c0, d.c1, d.c2, d.c3, d.y0, d.y1, d.capStart, d.capEnd);
      } else {
        g = new THREE.BufferGeometry();
        g.setAttribute(
          "position",
          new THREE.Float32BufferAttribute(revealPositions(d.pl, d.pr, d.y0, d.y1), 3),
        );
        g.setIndex([0, 1, 2, 0, 2, 3]);
        g.computeVertexNormals();
      }
      const m = new THREE.Mesh(g, pieceMat);
      m.userData.wallId = id;
      // Presentation rig: walls throw sun shadows onto the ground/other walls
      // and catch shadows from placed models. Inert while the sun is off.
      m.castShadow = true;
      m.receiveShadow = true;
      w.group.add(m);
      w.pieces.push(m);
      const dm = new THREE.Mesh(g, this.occDepthMat);
      dm.visible = false;
      dm.renderOrder = -1; // fill depth before the dots/walls draw
      w.group.add(dm);
      w.depthPieces.push(dm);
    }

    for (const bp of plan.panes) {
      const pm = new THREE.Mesh(this.geo, this.glassMat);
      pm.scale.set(bp.sx, bp.sy, bp.sz);
      pm.position.set(bp.px, bp.py, bp.pz);
      pm.rotation.y = bp.rotY;
      pm.userData.wallId = id;
      w.group.add(pm);
      w.panes.push(pm);
    }
    for (const bp of plan.leaves) {
      const leaf = new THREE.Mesh(this.geo, this.doorLeafMat);
      leaf.scale.set(bp.sx, bp.sy, bp.sz);
      leaf.position.set(bp.px, bp.py, bp.pz);
      leaf.rotation.y = bp.rotY;
      leaf.userData.wallId = id;
      leaf.castShadow = true;
      leaf.receiveShadow = true;
      // Black outline around the leaf's edges (shares the unit-box edge geo;
      // as a child it follows the leaf's scale/position/rotation).
      leaf.add(new THREE.LineSegments(this.doorLeafEdgeGeo, this.doorLeafBorderMat));
      w.group.add(leaf);
      w.leaves.push(leaf);
    }
    for (const arcPts of plan.swings) {
      const ageo = new THREE.BufferGeometry();
      ageo.setAttribute("position", new THREE.Float32BufferAttribute(arcPts, 3));
      const arc = new THREE.LineSegments(ageo, this.doorSwingMat);
      arc.userData.wallId = id;
      w.group.add(arc);
      w.swings.push(arc);
    }

    if (plan.border.length > 0) {
      const bgeo = new THREE.BufferGeometry();
      bgeo.setAttribute("position", new THREE.Float32BufferAttribute(plan.border, 3));
      w.border = new THREE.LineSegments(bgeo, this.occBorderMat);
      w.border.userData.wallId = id;
      w.group.add(w.border);
    }
    w.planKey = plan.key;
  }

  // ── Openings ──────────────────────────────────────────────────────────────
  addOpening(wallId: number, kind: OpeningKind): number | null {
    const w = this.walls.get(wallId);
    if (!w) return null;
    const id = this.nextO++;
    w.openings.push({ id, offset: this.wallLength(w) / 2, ...OPENING_DEFAULTS[kind] });
    this.layout(wallId);
    return id;
  }
  // Copy an opening (same kind + size) onto its wall, nudged one width to the
  // right (clamped to the wall) so it doesn't sit exactly on the original. The
  // copy starts UNLOCKED, ready to position. Returns the new opening's id.
  duplicateOpening(wallId: number, openingId: number): number | null {
    const f = this.findOpening(wallId, openingId);
    if (!f) return null;
    const { w, o } = f;
    const id = this.nextO++;
    const offset = this.clampOffset(w, o.width, o.offset + o.width + 0.1);
    w.openings.push({
      id,
      kind: o.kind,
      offset,
      width: o.width,
      height: o.height,
      sill: o.sill,
      hingeRight: o.hingeRight,
      swingNeg: o.swingNeg,
    });
    this.layout(wallId);
    return id;
  }
  removeOpening(wallId: number, openingId: number): void {
    const w = this.walls.get(wallId);
    if (!w) return;
    w.openings = w.openings.filter((o) => o.id !== openingId);
    this.layout(wallId);
  }
  updateOpening(wallId: number, openingId: number, patch: Partial<Opening>): void {
    const f = this.findOpening(wallId, openingId);
    if (!f) return;
    Object.assign(f.o, patch);
    this.layout(wallId);
  }
  // Flip a door's hinge to the other jamb / its swing to the other wall face.
  // No-ops on windows (which have no leaf or arc). Returns true if it applied,
  // so callers can fall through to other selections when it didn't.
  flipDoorHinge(wallId: number, openingId: number): boolean {
    const o = this.findOpening(wallId, openingId)?.o;
    if (!o || o.kind !== "door") return false;
    o.hingeRight = !o.hingeRight;
    this.layout(wallId);
    return true;
  }
  flipDoorSwing(wallId: number, openingId: number): boolean {
    const o = this.findOpening(wallId, openingId)?.o;
    if (!o || o.kind !== "door") return false;
    o.swingNeg = !o.swingNeg;
    this.layout(wallId);
    return true;
  }
  openingsOf(wallId: number): Opening[] {
    return this.walls.get(wallId)?.openings.map((o) => ({ ...o })) ?? [];
  }
  // Every wall id (live view — no array copy), for scanning all openings.
  wallIds(): IterableIterator<number> {
    return this.walls.keys();
  }
  // Like openingAtRay, but across EVERY wall — the nearest opening (with its wall
  // and ray distance) under a cursor ray, or null. Used to hover/select a door or
  // window, and to depth-compare it against placements behind/in front.
  openingAtRayAny(
    origin: THREE.Vector3,
    dir: THREE.Vector3,
  ): { wallId: number; openingId: number; distance: number } | null {
    let best: { wallId: number; openingId: number; distance: number } | null = null;
    for (const [wallId, w] of this.walls) {
      const hit = this.openingHit(w, origin, dir);
      if (hit && (best === null || hit.distance < best.distance)) {
        best = { wallId, openingId: hit.openingId, distance: hit.distance };
      }
    }
    return best;
  }

  // ── Selection + handles ───────────────────────────────────────────────────
  setSelected(id: number | null): void {
    const prev = this.selected;
    this.selected = id !== null && this.walls.has(id) ? id : null;
    if (prev !== null) this.layout(prev); // restore base material
    if (this.selected !== null) this.layout(this.selected); // selected material
    this.refreshHandles();
  }
  selectedId(): number | null {
    return this.selected;
  }
  // A wall reads as selected if it's the single selection or in the marquee set
  // — the same test the layout uses to paint the selected material. Drives which
  // walls show their dimension pill even when the global "Wall dimensions" toggle
  // is off.
  isSelected(id: number): boolean {
    return id === this.selected || this.highlighted.has(id);
  }

  // Marquee multi-selection: highlight an arbitrary set of walls (in addition
  // to any single `selected`). Only re-lays walls whose membership changed, so
  // it's cheap to call every frame while dragging the marquee.
  setHighlighted(ids: Iterable<number>): void {
    const next = new Set(ids);
    const prev = this.highlighted;
    // Update membership BEFORE re-laying, so layout() reads the new state when it
    // picks the material — otherwise added walls paint un-highlighted and removed
    // walls paint highlighted (one step behind).
    this.highlighted = next;
    for (const id of prev) if (!next.has(id)) this.layout(id);
    for (const id of next) if (!prev.has(id)) this.layout(id);
  }
  // Centerline segments of every wall, with id — for marquee hit-testing.
  wallSegments(): { id: number; ax: number; az: number; bx: number; bz: number }[] {
    const out = [];
    for (const [id, w] of this.walls) {
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      out.push({ id, ax: a.x, az: a.z, bx: b.x, bz: b.z });
    }
    return out;
  }
  // Expand `box` to cover the walls' real extent — computed from the corner
  // vertices (at floor and wall-top height), NOT setFromObject on the group,
  // so the hidden preview box + drag handles sitting at the origin can't drag
  // the layout's bounding box (and thus its center) toward the origin. Works
  // regardless of wall visibility (e.g. while in modeling mode).
  expandBounds(box: THREE.Box3): void {
    for (const v of this.verts.values()) {
      box.expandByPoint(this._bp.set(v.x, 0, v.z));
      box.expandByPoint(this._bp.set(v.x, this.wallHeight, v.z));
    }
  }
  private _bp = new THREE.Vector3();

  // Unique endpoint vertices shared by the given walls (for a group move).
  vertexIdsOf(wallIds: Iterable<number>): Set<number> {
    const vids = new Set<number>();
    for (const id of wallIds) {
      const w = this.walls.get(id);
      if (w) {
        vids.add(w.a);
        vids.add(w.b);
      }
    }
    return vids;
  }
  // Translate a set of vertices by (dx,dz) — skipping locked ones — and re-lay
  // every affected wall. Used to drag a marquee selection of walls as a group.
  translateVerts(vids: Iterable<number>, dx: number, dz: number): void {
    let moved = false;
    for (const vid of vids) {
      if (this.lockedVerts.has(vid)) continue;
      const v = this.verts.get(vid);
      if (!v) continue;
      v.x += dx;
      v.z += dz;
      this.relayoutAround(vid);
      moved = true;
    }
    if (moved) {
      this.autoOrientDimSides();
      this.refreshHandles();
    }
  }
  // Index of the selected wall in iteration (= serialize) order, or -1. Used to
  // re-select the "same" wall after an undo/redo rebuilds walls with fresh ids.
  selectedWallIndex(): number {
    if (this.selected === null) return -1;
    return [...this.walls.keys()].indexOf(this.selected);
  }
  selectWallByIndex(i: number): void {
    const ids = [...this.walls.keys()];
    if (i >= 0 && i < ids.length) this.setSelected(ids[i]);
  }
  private refreshHandles(): void {
    // Selecting a wall no longer shows its endpoint vertices — corners only
    // appear on hover / when a corner itself is selected (the in-place lock UI).
    for (const h of this.handles) h.visible = false;
  }

  // ── Picking ────────────────────────────────────────────────────────────────
  wallMeshes(): THREE.Mesh[] {
    const out: THREE.Mesh[] = [];
    if (!this.group.visible) return out; // hidden walls aren't pickable
    for (const w of this.walls.values()) out.push(...w.pieces, ...w.panes);
    return out;
  }
  wallIdForMesh(mesh: THREE.Object3D): number | null {
    return (mesh.userData?.wallId as number) ?? null;
  }
  handleMeshes(): THREE.Mesh[] {
    return this.handles.filter((h) => h.visible);
  }
  vertexIdForHandle(mesh: THREE.Object3D): number | null {
    return (mesh.userData?.vid as number) ?? null;
  }
  dimSideOf(id: number): DimSide {
    return this.walls.get(id)?.dimSide ?? 0;
  }
  // Change which side a wall's dimension is read on. Pure annotation — the
  // geometry doesn't change, only the reported length + label position. This
  // is the user's deliberate choice (a pill drag), so freeze it against the
  // auto-inner default.
  setDimSide(id: number, side: DimSide): void {
    const w = this.walls.get(id);
    if (w) {
      markFloorMutation();
      this.labelCache = null;
      w.dimSide = side;
      w.dimSideExplicit = true;
    }
  }

  // The "inner" face of a connected wall: the side its neighbouring walls bend
  // toward (the room interior for a closed loop). At each endpoint, project the
  // direction to the adjacent wall's far vertex onto this wall's left normal
  // and sum; the sign picks the side. Returns 0 when undeterminable — no
  // neighbour, or the two ends disagree (e.g. an S-bend) / are collinear.
  private innerSide(id: number): DimSide {
    const w = this.walls.get(id)!;
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const L = Math.hypot(dx, dz);
    if (L < 1e-6) return 1;
    const nlx = -dz / L; // left normal (= the +1 dimSide direction)
    const nlz = dx / L;
    let sum = 0;
    for (const [end, vid] of [[a, w.a], [b, w.b]] as const) {
      for (const [wid, e] of this.walls) {
        if (wid === id) continue;
        const other =
          e.a === vid ? this.verts.get(e.b) : e.b === vid ? this.verts.get(e.a) : null;
        if (!other) continue;
        const vx = other.x - end.x;
        const vz = other.z - end.z;
        const vl = Math.hypot(vx, vz);
        if (vl > 1e-6) sum += (vx * nlx + vz * nlz) / vl;
      }
    }
    // No neighbour to orient against (or symmetric) → default to a face, never
    // the centerline (dimSide 0 is no longer a user-facing option).
    return sum < -1e-6 ? -1 : 1;
  }

  // Re-derive the dimension side for every wall the user hasn't pinned,
  // defaulting connected walls to their inner (room) face. Called after any
  // topology / geometry change.
  private autoOrientDimSides(): void {
    this.labelCache = null;
    for (const [id, w] of this.walls) {
      if (!w.dimSideExplicit) w.dimSide = this.innerSide(id);
    }
  }

  // Reference geometry for a wall's dimension on the given side: the measured
  // SPAN (sx,sz)→(ex,ez), its midpoint (x,z) where the pill sits, and its
  // length. side 0 = centerline (vertex-to-vertex, the canonical, neighbour-
  // stable length). ±1 = the mitered left/right face, nudged outward by GAP so
  // the dimension line + pill clearly read as that side. (Which face is the
  // "room side" flips at convex corners, so the user picks it by dragging.)
  private refOf(
    id: number,
    side: DimSide,
  ): {
    x: number; z: number; length: number;
    sx: number; sz: number; ex: number; ez: number;
    wsx: number; wsz: number; wex: number; wez: number;
  } {
    const w = this.walls.get(id)!;
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    if (side === 0) {
      return {
        x: (a.x + b.x) / 2,
        z: (a.z + b.z) / 2,
        length: this.wallLength(w),
        sx: a.x, sz: a.z, ex: b.x, ez: b.z,
        wsx: a.x, wsz: a.z, wex: b.x, wez: b.z,
      };
    }
    const c = this.wallCorners(id);
    const p = side > 0 ? c.leftA : c.rightA; // measured face corner (A)
    const q = side > 0 ? c.leftB : c.rightB; // measured face corner (B)
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const L = Math.hypot(dx, dz) || 1;
    const onx = (-dz / L) * side; // unit outward normal on the MEASURED side
    const onz = (dx / L) * side;
    // Witness inner endpoints sit exactly ON the measured face corners, so the
    // witness lines touch the inside (showing what's measured) without any line
    // protruding into the room.
    const wsx = p.x;
    const wsz = p.z;
    const wex = q.x;
    const wez = q.z;
    // Dimension line + pill: across the wall to the opposite face, a DIM_GAP
    // beyond it — outside the measured room, decluttering the interior.
    const cross = WALL_THICKNESS + DIM_GAP;
    const sx = p.x - onx * cross;
    const sz = p.z - onz * cross;
    const ex = q.x - onx * cross;
    const ez = q.z - onz * cross;
    return {
      x: (sx + ex) / 2,
      z: (sz + ez) / 2,
      length: Math.hypot(q.x - p.x, q.z - p.z),
      sx, sz, ex, ez,
      wsx, wsz, wex, wez,
    };
  }

  info(id: number): WallInfo | null {
    const w = this.walls.get(id);
    if (!w) return null;
    const a = this.verts.get(w.a)!;
    const b = this.verts.get(w.b)!;
    // ax..bz stay the centerline (the drag math needs it); length is the
    // currently-referenced side, matching the on-wall pill + the edit field.
    return {
      id,
      ax: a.x,
      az: a.z,
      bx: b.x,
      bz: b.z,
      length: this.refOf(id, w.dimSide).length,
    };
  }
  // Per-wall label data: each wall's dimension pill position + length on its
  // chosen side, the measured span (for the dimension line), the side, and the
  // wall id so a picked sprite maps back to its wall.
  //
  // Cached: the label sync asks every active floor frame, refOf runs the
  // miter math per wall, and the answer only changes when wall geometry or a
  // dimSide does — exactly the sites that null the cache. Callers must treat
  // the returned array as read-only.
  private labelCache: WallLabelInfo[] | null = null;
  wallLabelInfos(): WallLabelInfo[] {
    if (this.labelCache) return this.labelCache;
    const out: WallLabelInfo[] = [];
    for (const [id, w] of this.walls) {
      const r = this.refOf(id, w.dimSide);
      out.push({ ...r, id, side: w.dimSide });
    }
    this.labelCache = out;
    return out;
  }
  // One wall's label info without building (or caching) the whole list — for
  // the in-place dimension editor, which tracks a single wall by id.
  wallLabelInfo(id: number): WallLabelInfo | null {
    const w = this.walls.get(id);
    if (!w) return null;
    return { ...this.refOf(id, w.dimSide), id, side: w.dimSide };
  }

  // Set a wall's CENTERLINE length, re-solving the connected walls so every
  // corner keeps its current angle (the room keeps its shape).
  setWallLength(id: number, length: number): void {
    this.solveAnglePreservingLength(id, Math.max(length, 1e-3));
  }

  // Set the wall's length as measured on its CURRENT dimension side, then
  // re-solve. The solve preserves every wall's direction, so a mitered face's
  // miter offset is constant — face length tracks centerline 1:1 — and we can
  // turn a face target into a centerline target with a single subtraction.
  setWallReferenceLength(id: number, length: number): void {
    const w = this.walls.get(id);
    if (!w || length <= 0) return;
    const target =
      w.dimSide === 0
        ? length
        : this.wallLength(w) + (length - this.refOf(id, w.dimSide).length);
    this.solveAnglePreservingLength(id, Math.max(target, 1e-3));
  }

  // Re-solve all vertex positions so the edited wall's centerline = `target`
  // while EVERY wall keeps its current direction (all corner angles preserved).
  // Linear least-squares: minimise movement from the current layout subject to
  // per-wall "keep direction" rows, the one length row, and fixed rows for the
  // anchor end + locked corners. One dense solve — no iteration.
  private solveAnglePreservingLength(setWallId: number, target: number): void {
    const setW = this.walls.get(setWallId);
    if (!setW) return;
    const ends = this.lengthEnds(setW);
    if (!ends) return; // both corners locked → length is fixed

    const ids = [...this.verts.keys()];
    const idx = new Map<number, number>();
    ids.forEach((vid, i) => idx.set(vid, i));
    const dim = 2 * ids.length;
    const M: number[][] = Array.from({ length: dim }, () => new Array(dim).fill(0));
    const rhs = new Array(dim).fill(0);

    const LAMBDA = 1; // stay-near-current (keeps it well-posed + minimal motion)
    // Constraint weights are high so residuals land WAY below display precision:
    // in a closed loop (a square), the wall opposite the edited one is pinned
    // only implicitly — through the soft "keep direction" rows closing the loop —
    // so a slack weight left it ~0.001 m off, enough to round to a different
    // value than its equal neighbour. At W=1e6 that residual is ~1e-5 m (the
    // matrix stays well-conditioned: λI floors the smallest eigenvalue at 1).
    const W = 1e6; // soft constraint weight
    const WPIN = 1e8; // pinned (anchor / locked) weight
    // stay-near term: LAMBDA·‖p − p₀‖²
    for (let i = 0; i < ids.length; i++) {
      const v = this.verts.get(ids[i])!;
      M[2 * i][2 * i] += LAMBDA;
      rhs[2 * i] += LAMBDA * v.x;
      M[2 * i + 1][2 * i + 1] += LAMBDA;
      rhs[2 * i + 1] += LAMBDA * v.z;
    }
    // Add a weighted linear constraint (Σ cₖ·p[k] = b) into the normal equations.
    const addRow = (coefs: [number, number][], b: number, weight: number) => {
      for (const [j, cj] of coefs) {
        rhs[j] += weight * cj * b;
        for (const [k, ck] of coefs) M[j][k] += weight * cj * ck;
      }
    };
    // Keep each wall pointing the same way: (p_b − p_a)·n = 0 (n = its normal).
    for (const w of this.walls.values()) {
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      const len = Math.hypot(b.x - a.x, b.z - a.z);
      if (len < 1e-6) continue;
      const nx = -(b.z - a.z) / len;
      const nz = (b.x - a.x) / len;
      const ia = idx.get(w.a)!;
      const ib = idx.get(w.b)!;
      addRow([[2 * ib, nx], [2 * ib + 1, nz], [2 * ia, -nx], [2 * ia + 1, -nz]], 0, W);
    }
    // The edited wall's length along its direction: (p_mover − p_anchor)·d = target.
    {
      const a = this.verts.get(ends.anchor)!;
      const m = this.verts.get(ends.mover)!;
      const len = Math.hypot(m.x - a.x, m.z - a.z) || 1;
      const dx = (m.x - a.x) / len;
      const dz = (m.z - a.z) / len;
      const ia = idx.get(ends.anchor)!;
      const im = idx.get(ends.mover)!;
      addRow([[2 * im, dx], [2 * im + 1, dz], [2 * ia, -dx], [2 * ia + 1, -dz]], target, W);
    }
    // Pin the anchor end + every locked corner at its current position.
    const pin = (vid: number) => {
      const v = this.verts.get(vid)!;
      const i = idx.get(vid)!;
      addRow([[2 * i, 1]], v.x, WPIN);
      addRow([[2 * i + 1, 1]], v.z, WPIN);
    };
    pin(ends.anchor);
    for (const vid of this.lockedVerts) if (idx.has(vid)) pin(vid);

    const sol = solveLinear(M, rhs);
    if (!sol) return;
    for (let i = 0; i < ids.length; i++) {
      const v = this.verts.get(ids[i])!;
      v.x = sol[2 * i];
      v.z = sol[2 * i + 1];
    }
    this.relayoutAll();
    this.autoOrientDimSides();
    this.refreshHandles();
    this.refreshLockMarkers(this.lockMarkersShowAll);
  }

  private relayoutAll(): void {
    for (const id of this.walls.keys()) this.layout(id);
  }

  // Raw wall endpoints for geometric queries (snap / hit tests). The snap
  // helpers run every frame while the Wall tool is aiming, and serialize()
  // builds the full persistence payload (openings included) just to read four
  // numbers per wall — this iterates the live data instead, allocating nothing.
  forEachSegment(fn: (ax: number, az: number, bx: number, bz: number) => void): void {
    for (const w of this.walls.values()) {
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      fn(a.x, a.z, b.x, b.z);
    }
  }

  // ── Persistence ────────────────────────────────────────────────────────────
  serialize(): {
    ax: number;
    az: number;
    bx: number;
    bz: number;
    dimSide: DimSide;
    dimSideExplicit: boolean;
    lockA: boolean;
    lockB: boolean;
    openings: {
      kind: OpeningKind;
      offset: number;
      width: number;
      height: number;
      sill: number;
      lockLeft?: boolean;
      lockRight?: boolean;
      lockTop?: boolean;
      lockBottom?: boolean;
      hingeRight?: boolean;
      swingNeg?: boolean;
    }[];
  }[] {
    const out = [];
    for (const w of this.walls.values()) {
      const a = this.verts.get(w.a)!;
      const b = this.verts.get(w.b)!;
      out.push({
        ax: a.x,
        az: a.z,
        bx: b.x,
        bz: b.z,
        dimSide: w.dimSide,
        dimSideExplicit: w.dimSideExplicit,
        lockA: this.lockedVerts.has(w.a),
        lockB: this.lockedVerts.has(w.b),
        openings: w.openings.map((o) => ({
          kind: o.kind,
          offset: o.offset,
          width: o.width,
          height: o.height,
          sill: o.sill,
          lockLeft: o.lockLeft,
          lockRight: o.lockRight,
          lockTop: o.lockTop,
          lockBottom: o.lockBottom,
          hingeRight: o.hingeRight,
          swingNeg: o.swingNeg,
        })),
      });
    }
    return out;
  }

  clearAll(): void {
    markFloorMutation();
    for (const w of this.walls.values()) {
      this.group.remove(w.group);
      if (w.proj) {
        this.projGroup.remove(w.proj);
        w.proj.geometry.dispose();
      }
    }
    this.walls.clear();
    this.verts.clear();
    this.incident.clear();
    this.labelCache = null;
    this.lockedVerts.clear();
    this.ceilingDirty = true;
    this.seeThruCeilingDirty = true;
    // Drop the stale roof outright so a freshly loaded layout never flashes the
    // old room's ceiling before the next see-through rebuild.
    if (this.seeThruCeiling) {
      this.group.remove(this.seeThruCeiling);
      this.seeThruCeiling.geometry.dispose();
      this.seeThruCeiling = null;
    }
    this.refreshLockMarkers(this.lockMarkersShowAll);
    this.setSelected(null);
  }

  loadWalls(
    walls: {
      ax: number;
      az: number;
      bx: number;
      bz: number;
      dimSide?: DimSide;
      dimSideExplicit?: boolean;
      lockA?: boolean;
      lockB?: boolean;
      openings: {
        kind: OpeningKind;
        offset: number;
        width: number;
        height: number;
        sill: number;
        lockLeft?: boolean;
        lockRight?: boolean;
        lockTop?: boolean;
        lockBottom?: boolean;
        hingeRight?: boolean;
        swingNeg?: boolean;
      }[];
    }[],
  ): void {
    this.clearAll();
    for (const w of walls) {
      const id = this.addWall(w.ax, w.az, w.bx, w.bz);
      // Restore a pinned side. Newer plans carry the explicit flag; for older
      // ones a saved non-zero side must have been a manual drag (the default
      // was centerline), so treat it as pinned too. A saved centerline (0) is no
      // longer a valid option — leave it unpinned so it re-orients to a face.
      const pinned = (w.dimSideExplicit ?? (w.dimSide ? true : false)) && w.dimSide !== 0;
      if (pinned) this.setDimSide(id, w.dimSide ?? 1);
      const e = this.walls.get(id)!;
      if (w.lockA) this.lockedVerts.add(e.a);
      if (w.lockB) this.lockedVerts.add(e.b);
      for (const o of w.openings) {
        const oid = this.addOpening(id, o.kind);
        if (oid !== null) {
          this.updateOpening(id, oid, {
            offset: o.offset,
            width: o.width,
            height: o.height,
            sill: o.sill,
            lockLeft: o.lockLeft,
            lockRight: o.lockRight,
            lockTop: o.lockTop,
            lockBottom: o.lockBottom,
            hingeRight: o.hingeRight,
            swingNeg: o.swingNeg,
          });
        }
      }
    }
    // Full topology is in place now — orient every non-pinned wall to its inner face.
    this.autoOrientDimSides();
  }

  // ── Draw preview ───────────────────────────────────────────────────────────
  setPreview(ax: number, az: number, bx: number, bz: number): void {
    const dx = bx - ax;
    const dz = bz - az;
    const len = Math.hypot(dx, dz);
    this.preview.scale.set(Math.max(len, 1e-4), this.wallHeight, WALL_THICKNESS);
    this.preview.position.set((ax + bx) / 2, this.wallHeight / 2, (az + bz) / 2);
    this.preview.rotation.y = -Math.atan2(dz, dx);
    this.preview.visible = true;
  }
  hidePreview(): void {
    this.preview.visible = false;
  }
}
